import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Watermark from "@/components/app/Watermark";
import AntiCopyGuard from "@/components/app/AntiCopyGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, LogOut, UserPlus, Users, FolderTree, Inbox, Check, X, History, Search, Upload, FileText, Send, Sprout, TrendingUp, MapPin, ImagePlus, BarChart3, ShieldCheck, UserCircle2, Settings, ArrowUp, ArrowDown, Eye, EyeOff, Activity } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { formatIST } from "@/lib/time";
import CommandCentreTab from "@/components/ops/CommandCentreTab";
import StaffTab from "@/components/staff/StaffTab";
import PrepMethodTab from "@/components/ops/PrepMethodTab";

export default function AdminDashboard({ user, onLogout }) {
  const [tab, setTab] = useState("catalog");
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [audit, setAudit] = useState([]);
  const [globalQ, setGlobalQ] = useState("");
  const [searchRes, setSearchRes] = useState(null);
  const [nav, setNav] = useState({ items: [] });
  const [navOpen, setNavOpen] = useState(false);
  const NAV_ICON = { FolderTree, Sprout, Users, MapPin, UserCircle2, BarChart3, Inbox, History, Activity, FileText };
  useEffect(() => {
    api.get("/nav-config").then(r => setNav(r.data)).catch(() => {});
  }, []);
  const reloadNav = () => api.get("/nav-config").then(r => setNav(r.data));

  const [catDlg, setCatDlg] = useState({ open: false, mode: "add", cat: null });
  const [itemDlg, setItemDlg] = useState({ open: false, mode: "add", item: null });
  const [userDlg, setUserDlg] = useState({ open: false, editing: null });
  const [outlets, setOutlets] = useState([]);

  const load = async () => {
    const [c, s, u, a, o] = await Promise.all([
      api.get("/categories"), api.get("/suggestions"), api.get("/users"), api.get("/audit-logs"), api.get("/outlets")
    ]);
    setCats(c.data); setSuggestions(s.data); setUsers(u.data); setAudit(a.data); setOutlets(o.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!globalQ.trim()) { setSearchRes(null); return; }
    const t = setTimeout(() => api.get(`/search?q=${encodeURIComponent(globalQ)}`).then(r => setSearchRes(r.data)), 250);
    return () => clearTimeout(t);
  }, [globalQ]);
  useEffect(() => {
    if (selectedCat) api.get(`/items?category_id=${selectedCat.id}`).then(r => setItems(r.data));
    else setItems([]);
  }, [selectedCat]);

  const reloadItems = () => selectedCat && api.get(`/items?category_id=${selectedCat.id}`).then(r => setItems(r.data));

  const openStats = suggestions.filter(s => s.status === "open").length;

  return (
    <AntiCopyGuard>
      <div className="min-h-screen relative" style={{ background: "linear-gradient(180deg, #FBF5E4 0%, #F5EEDA 50%, #EFE7CB 100%)" }}>
        <Watermark email={user.email} dark />
        <header className="sticky top-0 z-30 border-b" style={{ background: "rgba(255,253,246,0.96)", backdropFilter: "blur(10px)", borderColor: "#E4D6A8" }}>
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3 px-4 md:px-6 py-3 md:py-4">
            <div className="min-w-0">
              <div className="font-mono2 text-[10px] tracking-[0.35em] uppercase" style={{ color: "#B4841F" }}>Admin · Command Center</div>
              <div className="font-display text-xl leading-none mt-1" style={{ color: "#2b1a0d" }}>Chai Chaska Cafe · Vault</div>
            </div>
            <div className="flex items-center gap-3 flex-wrap ml-auto w-full sm:w-auto">
              <div className="relative flex-1 min-w-[180px] md:w-72 md:flex-none">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#B4841F" }} />
                <Input data-testid="admin-global-search" value={globalQ} onChange={e => setGlobalQ(e.target.value)}
                  placeholder="Search everything" className="pl-9 h-9 bg-white border-[#E4D6A8]" />
              </div>
              <span className="hidden md:inline text-xs font-mono2 uppercase tracking-widest truncate max-w-[220px]" style={{ color: "#7a6349" }}>{user.email}</span>
              <button data-testid="logout-btn" onClick={onLogout} className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest hover:text-[#B4841F]" style={{ color: "#7a6349" }}>
                <LogOut size={14} /> Sign out
              </button>
            </div>
          </div>
        </header>

        <main className="relative z-20 max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
          {/* Global scrollable table styles for mobile — every table below scrolls horizontally on small screens. */}
          <style>{`
            @media (max-width: 767px) {
              .cc-scroll-x { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
              .cc-scroll-x > table { min-width: 720px; }
            }
          `}</style>
          <Tabs value={tab} onValueChange={setTab} orientation="vertical">
            <div className="grid md:grid-cols-[220px_1fr] gap-4 md:gap-6">
              {/* LEFT VERTICAL SIDEBAR NAV — mobile: horizontal-scrolling rail */}
              <div className="md:sticky md:top-24 md:self-start min-w-0">
                <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 pb-1">
                  <TabsList className="bg-white border border-[#E4D6A8] p-1 rounded-2xl md:flex-col md:h-auto md:w-full md:items-stretch flex-nowrap w-max md:w-auto md:space-y-1">
                    {(nav.items || []).filter(it => !it.hidden).map(it => {
                      const Ic = NAV_ICON[it.icon] || FolderTree;
                      const badge = it.key === "suggestions" && openStats > 0
                        ? <Badge className="ml-2 bg-[#F5C242] text-black">{openStats}</Badge>
                        : null;
                      return (
                        <div key={it.key}>
                          <TabsTrigger data-testid={`tab-${it.key}`} value={it.key}
                            className="justify-start rounded-xl data-[state=active]:bg-[#F5C242] data-[state=active]:text-black w-full">
                            <Ic size={14} className="mr-2" />{it.label}{badge}
                          </TabsTrigger>
                          {(it.children && it.children.length > 0) && (
                            <div className="pl-6 py-1 space-y-0.5">
                              {it.children.slice().sort((a,b) => (a.order||0) - (b.order||0)).map(ch => (
                                <button key={ch.id} data-testid={`subnav-${it.key}-${ch.id}`}
                                  onClick={() => { setTab(it.key); setGlobalQ(ch.label); }}
                                  className="w-full text-left text-xs px-3 py-1.5 rounded-lg text-[#5b4530] hover:bg-[#F5EFE0] font-mono2 tracking-wide">
                                  · {ch.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <button data-testid="nav-configure" onClick={() => setNavOpen(true)}
                      className="mt-2 text-[10px] font-mono2 uppercase tracking-widest px-3 py-2 rounded-lg text-[#7a6349] hover:bg-[#F5EFE0] flex items-center gap-2">
                      <Settings size={12}/> Configure sidebar
                    </button>
                  </TabsList>
                </div>
              </div>

              {/* MAIN CONTENT COLUMN */}
              <div className="min-w-0">

            {searchRes && (
              <div className="mt-4 rounded-2xl border border-[#E4D6A8] p-4 bg-white" style={{ color: "#2b1a0d" }}>
                <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-[#B4841F] mb-2">Search {`"${globalQ}"`}</div>
                {searchRes.categories.length > 0 && <div className="mb-2 text-sm"><b>Categories:</b> {searchRes.categories.map(c => <button key={c.id} onClick={() => { setSelectedCat(c); setTab("catalog"); setGlobalQ(""); }} className="inline-block ml-2 px-2 py-1 rounded bg-[#F5EFE0] text-[#3B2519] hover:bg-[#F5C242]">{c.name}</button>)}</div>}
                {searchRes.items.length > 0 && <div className="mb-2 text-sm"><b>Items:</b> {searchRes.items.slice(0,20).map(it => <span key={it.id} className="inline-block ml-2 px-2 py-1 rounded bg-[#F5EFE0] text-[#3B2519]">{it.name} <span className="opacity-60">₹{it.sp}</span></span>)}</div>}
                {searchRes.ingredient_matches.length > 0 && <div className="text-sm"><b>Ingredients found in:</b> {searchRes.ingredient_matches.slice(0,20).map(m => <span key={m.item.id} className="inline-block ml-2 px-2 py-1 rounded bg-[#F5EFE0] text-[#3B2519]">{m.item.name}</span>)}</div>}
              </div>
            )}

            <TabsContent value="prep" className="mt-6">
              {tab === "prep" && <PrepMethodTab canEdit email={user.email} />}
            </TabsContent>

            <TabsContent value="catalog" className="mt-6">
              <div className="grid md:grid-cols-[280px_1fr] gap-6">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-[#7a6349]">Categories</div>
                    <Button data-testid="add-category-btn" size="sm" variant="ghost" className="h-7 text-[#B4841F] hover:text-[#8a5a1a]"
                      onClick={() => setCatDlg({ open: true, mode: "add", cat: null })}>
                      <Plus size={14} /> Add
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {cats.map(c => (
                      <div key={c.id} className={`group flex items-center rounded-xl border ${selectedCat?.id === c.id ? "bg-[#F5C242] text-black border-transparent" : "border-[#E4D6A8] hover:bg-[#EFE3C6]"}`}>
                        <button data-testid={`cat-${c.id}`} onClick={() => setSelectedCat(c)} className="flex-1 text-left px-4 py-3">
                          <div className="font-display text-lg">{c.name}</div>
                        </button>
                        <button data-testid={`edit-cat-${c.id}`} onClick={() => setCatDlg({ open: true, mode: "edit", cat: c })}
                          className="px-2 opacity-60 hover:opacity-100"><Edit3 size={14} /></button>
                        <button data-testid={`del-cat-${c.id}`} onClick={async () => {
                          if (!window.confirm(`Delete "${c.name}" and all its items?`)) return;
                          await api.delete(`/categories/${c.id}`); toast.success("Deleted");
                          if (selectedCat?.id === c.id) setSelectedCat(null); load();
                        }} className="px-3 opacity-60 hover:opacity-100 hover:text-red-500"><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  {!selectedCat ? (
                    <div className="rounded-2xl border border-dashed border-[#E4D6A8] p-16 text-center text-[#7a6349]">
                      Select a category to manage its items
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                        <h2 className="font-display text-3xl flex-1 min-w-0 break-words">{selectedCat.name}</h2>
                        <div className="flex items-center gap-2 flex-wrap">
                          <BulkImportButton onDone={() => { load(); reloadItems(); }} />
                          <Button data-testid="add-item-btn" className="rounded-full bg-[#F5C242] text-black hover:bg-[#e9a722] btn-lift whitespace-nowrap"
                            onClick={() => setItemDlg({ open: true, mode: "add", item: null })}>
                            <Plus size={14} className="mr-1" /> New Item
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {items.map(it => (
                          <div key={it.id} className="rounded-xl border border-[#E4D6A8] p-4 flex items-center gap-4 hover:border-[#F5C242]/40">
                            <div className="flex-1">
                              <div className="font-display text-xl">{it.name}</div>
                              <div className="text-xs font-mono2 uppercase tracking-widest text-[#7a6349] mt-1">
                                SP ₹{it.sp}{it.making_cost != null && <> · Cost ₹{Number(it.making_cost).toFixed(2)}</>}{it.gross_margin != null && <> · GM ₹{Number(it.gross_margin).toFixed(2)}{it.gross_margin_pct != null && ` (${Number(it.gross_margin_pct).toFixed(2)}%)`}</>} · {it.ingredients?.length || 0} ingredients
                              </div>
                            </div>
                            <Button data-testid={`pdf-item-${it.id}`} size="sm" variant="ghost"
                              onClick={async () => {
                                const t = localStorage.getItem("cc_token");
                                const url = `${process.env.REACT_APP_BACKEND_URL}/api/items/${it.id}/pdf`;
                                const res = await fetch(url, { headers: { Authorization: `Bearer ${t}` } });
                                if (!res.ok) return toast.error("PDF failed");
                                const blob = await res.blob();
                                const link = document.createElement("a");
                                link.href = URL.createObjectURL(blob);
                                link.download = `${it.name}.pdf`; link.click();
                              }}>
                              <FileText size={14} />
                            </Button>
                            <Button data-testid={`edit-item-${it.id}`} size="sm" variant="ghost"
                              onClick={() => setItemDlg({ open: true, mode: "edit", item: it })}>
                              <Edit3 size={14} />
                            </Button>
                            <Button data-testid={`del-item-${it.id}`} size="sm" variant="ghost"
                              onClick={async () => {
                                if (!window.confirm(`Delete "${it.name}"?`)) return;
                                await api.delete(`/items/${it.id}`); toast.success("Deleted"); reloadItems();
                              }}>
                              <Trash2 size={14} className="text-red-500" />
                            </Button>
                          </div>
                        ))}
                        {items.length === 0 && <div className="text-[#7a6349]">No items yet</div>}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="users" className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-display text-3xl" style={{ color: "#2b1a0d" }}>Authorised Franchise Users</h2>
                <Button data-testid="add-user-btn" onClick={() => setUserDlg({ open: true, editing: null })}
                  className="rounded-full bg-[#F5C242] text-black hover:bg-[#e9a722] btn-lift">
                  <UserPlus size={14} className="mr-2" /> Grant Access
                </Button>
              </div>
              <div className="rounded-2xl border border-[#E4D6A8] overflow-hidden bg-white cc-scroll-x">
                <table className="w-full text-sm">
                  <thead className="bg-[#F5EFE0] font-mono2 text-[10px] uppercase tracking-widest" style={{ color: "#7a6349" }}>
                    <tr><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Name</th>
                      <th className="text-left px-4 py-3">Store · Outlet</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
                  </thead>
                  <tbody style={{ color: "#2b1a0d" }}>
                    {users.map(u => (
                      <tr key={u.id} className="border-t border-[#E4D6A8]">
                        <td className="px-4 py-3">{u.email}</td>
                        <td className="px-4 py-3">{u.name || "—"}</td>
                        <td className="px-4 py-3">{u.store || outlets.find(o=>o.id===u.outlet_id)?.name || "—"}</td>
                        <td className="px-4 py-3">
                          {u.active ? <Badge className="bg-emerald-100 text-emerald-700 border-transparent">Active</Badge>
                            : <Badge className="bg-red-100 text-red-700 border-transparent">Revoked</Badge>}
                          {u.can_edit && <Badge className="ml-2 bg-blue-100 text-blue-700 border-transparent" data-testid={`can-edit-${u.id}`}>Editor</Badge>}
                          <Badge className="ml-2 bg-[#F5EFE0] text-[#3B2519] border-transparent" data-testid={`max-devices-${u.id}`}>{u.max_devices || 1} device{(u.max_devices || 1) === 1 ? "" : "s"}</Badge>
                          {(u.device_fingerprint || (u.device_fingerprints && u.device_fingerprints.length > 0)) && <Badge className="ml-2 bg-[#F5C242]/30 text-[#8a5a1a] border-transparent" data-testid={`device-bound-${u.id}`}>{Math.max((u.device_fingerprints || []).length, u.device_fingerprint ? 1 : 0)}/{u.max_devices || 1} bound</Badge>}
                          {u.device_violation_count > 0 && <Badge className="ml-2 bg-orange-100 text-orange-700 border-transparent" data-testid={`violations-${u.id}`}>{u.device_violation_count}/3 warnings</Badge>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button data-testid={`edit-user-${u.id}`} variant="ghost" size="sm"
                            onClick={() => setUserDlg({ open: true, editing: u })}>
                            <Edit3 size={14} />
                          </Button>
                          {(u.device_fingerprint || u.device_violation_count > 0 || !u.active) && (
                            <Button data-testid={`reset-device-${u.id}`} variant="ghost" size="sm"
                              onClick={async () => {
                                if (!window.confirm(`Reset device binding and warnings for ${u.email}?`)) return;
                                await api.post(`/users/${u.id}/reset-device`);
                                toast.success("Device reset · warnings cleared · access restored");
                                load();
                              }}>
                              Reset device
                            </Button>
                          )}
                          <Button data-testid={`toggle-user-${u.id}`} variant="ghost" size="sm"
                            onClick={async () => { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); }}>
                            {u.active ? "Revoke" : "Restore"}
                          </Button>
                          <Button data-testid={`del-user-${u.id}`} variant="ghost" size="sm"
                            onClick={async () => {
                              if (!window.confirm(`Remove ${u.email}?`)) return;
                              await api.delete(`/users/${u.id}`); toast.success("Removed"); load();
                            }}>
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center" style={{ color: "#7a6349" }}>No franchise users yet. Grant access to your first store.</td></tr>}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="suggestions" className="mt-6">
              <SuggestionsInbox suggestions={suggestions} outlets={outlets} users={users} onDone={load} />
            </TabsContent>

            <TabsContent value="command" className="mt-6">
              <CommandCentreTab />
            </TabsContent>

            <TabsContent value="sessions" className="mt-6">
              <SessionsTab />
            </TabsContent>

            <TabsContent value="audit" className="mt-6">
              <h2 className="font-display text-3xl mb-4">Audit Log</h2>
              <div className="rounded-2xl border border-[#E4D6A8] overflow-hidden cc-scroll-x">
                <table className="w-full text-sm">
                  <thead className="bg-[#F5EFE0] text-[#7a6349] font-mono2 text-[10px] uppercase tracking-widest">
                    <tr><th className="text-left px-4 py-3">When</th><th className="text-left px-4 py-3">Actor</th>
                      <th className="text-left px-4 py-3">Action</th><th className="text-left px-4 py-3">Entity</th>
                      <th className="text-left px-4 py-3">Details</th></tr>
                  </thead>
                  <tbody>
                    {audit.map(a => (
                      <tr key={a.id} className="border-t border-[#E4D6A8]">
                        <td className="px-4 py-3 font-mono2 text-xs">{formatIST(a.timestamp)}</td>
                        <td className="px-4 py-3">{a.actor}</td>
                        <td className="px-4 py-3"><Badge variant="outline" className="uppercase text-[10px]">{a.action}</Badge></td>
                        <td className="px-4 py-3">{a.entity}</td>
                        <td className="px-4 py-3 text-[#3d2a19]">{a.details}</td>
                      </tr>
                    ))}
                    {audit.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-[#7a6349]">No activity yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="ingredients" className="mt-6">
              <IngredientsTab onDone={load} />
            </TabsContent>

            <TabsContent value="outlets" className="mt-6">
              <OutletsTab />
            </TabsContent>

            <TabsContent value="reports" className="mt-6">
              <ReportsTab />
            </TabsContent>
            <TabsContent value="staff" className="mt-6"><StaffTab /></TabsContent>
              </div>
            </div>
          </Tabs>
        </main>

        <CategoryDialog dlg={catDlg} setDlg={setCatDlg} onDone={load} />
        <ItemDialog dlg={itemDlg} setDlg={setItemDlg} cats={cats} selectedCat={selectedCat} onDone={reloadItems} />
        <UserDialog dlg={userDlg} setDlg={setUserDlg} cats={cats} onDone={load} />
        <NavConfigDialog open={navOpen} onOpenChange={setNavOpen} nav={nav} onSaved={reloadNav} />
      </div>
    </AntiCopyGuard>
  );
}

function SuggestionsInbox({ suggestions, outlets, users, onDone }) {
  const [status, setStatus] = useState("all");
  const [outletId, setOutletId] = useState("all");
  const [itemQ, setItemQ] = useState("");
  const [presets, setPresets] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cc_sug_presets") || "[]"); } catch { return []; }
  });
  const savePresets = (next) => {
    setPresets(next);
    try { localStorage.setItem("cc_sug_presets", JSON.stringify(next)); } catch { /* quota */ }
  };
  const outletName = (oid) => oid === "all" ? "All outlets" : oid === "unassigned" ? "Unassigned" : (outlets.find(o => o.id === oid)?.name || "Outlet");
  const buildLabel = () => {
    const parts = [];
    if (outletId !== "all") parts.push(outletName(outletId));
    if (status !== "all") parts.push(status[0].toUpperCase() + status.slice(1));
    if (itemQ.trim()) parts.push(`"${itemQ.trim().slice(0, 14)}"`);
    return parts.length ? parts.join(" · ") : "All suggestions";
  };
  const pinCurrent = () => {
    const label = buildLabel();
    if (presets.some(p => p.label === label && p.status === status && p.outletId === outletId && (p.itemQ || "") === itemQ)) {
      toast.info("Already pinned"); return;
    }
    savePresets([...presets, { id: Date.now(), label, status, outletId, itemQ }]);
    toast.success("Filter pinned");
  };
  const applyPreset = (p) => { setStatus(p.status); setOutletId(p.outletId); setItemQ(p.itemQ || ""); };
  const removePreset = (id) => savePresets(presets.filter(p => p.id !== id));

  const emailsForOutlet = (oid) => new Set(users.filter(u => u.outlet_id === oid).map(u => u.email));
  const filtered = suggestions.filter(s => {
    if (status !== "all" && s.status !== status) return false;
    if (outletId !== "all") {
      if (outletId === "unassigned") {
        const noOutletEmails = new Set(users.filter(u => !u.outlet_id).map(u => u.email));
        if (!noOutletEmails.has(s.user_email)) return false;
      } else {
        if (!emailsForOutlet(outletId).has(s.user_email)) return false;
      }
    }
    if (itemQ && !s.item_name.toLowerCase().includes(itemQ.toLowerCase())) return false;
    return true;
  });
  const openCount = filtered.filter(s => s.status === "open").length;
  const hasNonDefaultFilter = status !== "all" || outletId !== "all" || itemQ.trim().length > 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl" style={{ color: "#2b1a0d" }}>Discrepancy Suggestions</h2>
          <p className="text-sm mt-1" style={{ color: "#7a6349" }}>Showing {filtered.length} of {suggestions.length} · {openCount} open</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select data-testid="filter-status" value={status} onChange={e => setStatus(e.target.value)}
            className="h-9 px-3 rounded-md border border-[#E4D6A8] bg-white text-sm">
            <option value="all">All statuses</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
          </select>
          <select data-testid="filter-outlet" value={outletId} onChange={e => setOutletId(e.target.value)}
            className="h-9 px-3 rounded-md border border-[#E4D6A8] bg-white text-sm">
            <option value="all">All outlets</option>
            {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            <option value="unassigned">— Unassigned —</option>
          </select>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
            <Input data-testid="filter-item-search" value={itemQ} onChange={e => setItemQ(e.target.value)}
              placeholder="Filter by item" className="pl-9 h-9 bg-white border-[#E4D6A8] w-48" />
          </div>
          <Button data-testid="pin-filter-btn" size="sm" disabled={!hasNonDefaultFilter} onClick={pinCurrent}
            className="rounded-full bg-[#3B2519] hover:bg-[#5a3826] text-[#F5C242] disabled:opacity-40">
            + Pin filter
          </Button>
        </div>
      </div>

      {presets.length > 0 && (
        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="filter-presets">
          <span className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#7a6349" }}>Pinned:</span>
          {presets.map(p => (
            <span key={p.id} className="inline-flex items-center gap-1 text-xs rounded-full pl-3 pr-1 py-1 border"
              style={{ background: "#FFF8E7", borderColor: "#E4D6A8", color: "#3B2519" }} data-testid={`preset-${p.id}`}>
              <button onClick={() => applyPreset(p)} className="font-medium">{p.label}</button>
              <button onClick={() => removePreset(p.id)} title="Unpin"
                className="w-5 h-5 rounded-full hover:bg-[#F5C242] flex items-center justify-center"
                data-testid={`preset-remove-${p.id}`}>
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="space-y-3">
        {filtered.map(s => <SuggestionCard key={s.id} s={s} onDone={onDone} />)}
        {filtered.length === 0 && <div style={{ color: "#7a6349" }}>No suggestions match these filters.</div>}
      </div>
    </div>
  );
}

function SuggestionCard({ s, onDone }) {
  const [reply, setReply] = useState("");
  const [replyImage, setReplyImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const submit = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/suggestions/${s.id}/reply`, { message: reply, image_url: replyImage || null });
      toast.success("Reply sent to franchise");
      setReply(""); setReplyImage(""); onDone();
    } catch { toast.error("Failed"); } finally { setSending(false); }
  };
  const uploadImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const t = localStorage.getItem("cc_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads`, {
        method: "POST", headers: { Authorization: `Bearer ${t}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      setReplyImage(data.url); toast.success("Photo attached");
    } catch (err) { toast.error(err.message || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className="rounded-xl border border-[#E4D6A8] p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-display text-lg">{s.item_name}</span>
            <Badge variant="outline" className="text-[10px] uppercase tracking-widest">{s.field}</Badge>
            {s.ingredient_name && <Badge variant="outline" className="text-[10px]">{s.ingredient_name}</Badge>}
            {s.status === "open"
              ? <Badge className="bg-[#F5C242] text-black">Open</Badge>
              : <Badge className="bg-emerald-500/20 text-emerald-400 border-transparent">Resolved</Badge>}
          </div>
          <div className="text-sm text-[#3d2a19] whitespace-pre-wrap">{s.message}</div>
          {s.image_url && (
            <div className="mt-2 relative inline-block group" data-testid={`sug-image-${s.id}`}>
              <a href={s.image_url} target="_blank" rel="noopener noreferrer" title="Click to view full size">
                <img src={s.image_url} alt="Suggestion attachment"
                  className="max-h-48 rounded-lg border border-[#E4D6A8] transition-all duration-200"
                  style={{ filter: "blur(10px)" }}
                  draggable={false}
                  onMouseEnter={(e) => (e.currentTarget.style.filter = "blur(0)")}
                  onMouseLeave={(e) => (e.currentTarget.style.filter = "blur(10px)")} />
              </a>
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-white font-mono2 text-[10px] uppercase tracking-widest group-hover:opacity-0 transition-opacity"
                style={{ background: "rgba(59,37,25,0.35)", borderRadius: "0.5rem" }}>
                Hover to reveal · click to open
              </div>
            </div>
          )}
          <div className="mt-2 text-xs font-mono2 uppercase tracking-widest text-[#7a6349]">
            {s.user_email} · {formatIST(s.created_at)}
          </div>
          {s.replies && s.replies.length > 0 && (
            <div className="mt-3 space-y-2 pl-3 border-l-2 border-[#F5C242]/40">
              {s.replies.map(r => (
                <div key={r.id} className="text-sm">
                  <div className="text-[10px] font-mono2 uppercase tracking-widest text-[#7a6349] mb-1">
                    {r.author_role === "admin" ? "🛡 Admin" : r.author_email} · {formatIST(r.created_at)}
                  </div>
                  <div className="text-[#2b1a0d] whitespace-pre-wrap">{r.message}</div>
                  {r.image_url && (
                    <a href={r.image_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-1" data-testid={`reply-image-${r.id}`}>
                      <img src={r.image_url} alt="Reply attachment" className="max-h-32 rounded border border-[#E4D6A8]" draggable={false} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 space-y-2">
            <div className="flex gap-2 items-start">
              <Textarea data-testid={`reply-input-${s.id}`} rows={2} value={reply} onChange={e => setReply(e.target.value)}
                placeholder="Reply to franchise…" className="bg-white border-[#E4D6A8] text-sm" />
              <Button data-testid={`send-reply-${s.id}`} size="sm" disabled={!reply.trim() || sending} onClick={submit}
                className="bg-[#F5C242] text-black hover:bg-[#e9a722] shrink-0">
                <Send size={14} className="mr-1" /> Send
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {replyImage && (
                <img src={replyImage} alt="" className="w-10 h-10 rounded border border-[#E4D6A8] object-cover" draggable={false} />
              )}
              <label className={`inline-flex items-center gap-1 text-[10px] font-mono2 uppercase tracking-widest cursor-pointer px-2 py-1 rounded border border-[#E4D6A8] bg-white ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                style={{ color: "#8a5a1a" }}>
                <input type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid={`reply-image-input-${s.id}`} />
                <ImagePlus size={12} /> {uploading ? "Uploading…" : (replyImage ? "Replace photo" : "Attach resolved photo")}
              </label>
              {replyImage && (
                <button type="button" onClick={() => setReplyImage("")}
                  className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#c0392b" }}>Remove</button>
              )}
            </div>
          </div>
        </div>
        {s.status === "open" && (
          <Button data-testid={`resolve-${s.id}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700"
            onClick={async () => { await api.patch(`/suggestions/${s.id}?status=resolved`); onDone(); }}>
            <Check size={14} className="mr-1" /> Resolve
          </Button>
        )}
      </div>
    </div>
  );
}

function BulkImportButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const t = localStorage.getItem("cc_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/import/excel`, {
        method: "POST", headers: { Authorization: `Bearer ${t}` }, body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Import failed");
      setStats(data.stats); setOpen(true); onDone();
      toast.success("Import complete");
    } catch (e) { toast.error(e.message || "Import failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <>
      <label>
        <input type="file" accept=".xlsx,.xls" onChange={onFile} className="hidden" data-testid="bulk-import-file" />
        <span className={`inline-flex items-center gap-2 px-4 h-10 rounded-full border border-[#E4D6A8] bg-white text-[#8a5a1a] hover:bg-[#F5EFE0] cursor-pointer text-sm whitespace-nowrap ${busy ? "opacity-60 pointer-events-none" : ""}`}>
          <Upload size={14} /> {busy ? "Importing…" : "Bulk Import Excel"}
        </span>
      </label>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#F5EFE0] border-[#E4D6A8] text-[#2b1a0d]">
          <DialogHeader><DialogTitle className="font-display text-2xl">Import complete</DialogTitle></DialogHeader>
          {stats && (
            <div className="space-y-2 font-mono2 text-sm">
              <div>Categories created: <b>{stats.categories_created}</b></div>
              <div>Items created: <b>{stats.items_created}</b></div>
              <div>Items updated: <b>{stats.items_updated}</b></div>
              <div>Ingredients updated: <b>{stats.ingredients_updated}</b></div>
            </div>
          )}
          <DialogFooter><Button onClick={() => setOpen(false)} className="bg-[#F5C242] text-black">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CategoryDialog({ dlg, setDlg, onDone }) {
  const [name, setName] = useState(""); const [desc, setDesc] = useState("");
  useEffect(() => { setName(dlg.cat?.name || ""); setDesc(dlg.cat?.description || ""); }, [dlg]);
  const save = async () => {
    try {
      if (dlg.mode === "add") await api.post("/categories", { name, description: desc });
      else await api.patch(`/categories/${dlg.cat.id}`, { name, description: desc });
      toast.success("Saved"); setDlg({ open: false, mode: "add", cat: null }); onDone();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })}>
      <DialogContent className="bg-white border-[#E4D6A8]" style={{ color: "#2b1a0d" }}>
        <DialogHeader><DialogTitle className="font-display text-2xl">{dlg.mode === "add" ? "New Category" : "Edit Category"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="cat-name-input" value={name} onChange={e => setName(e.target.value)} /></div>
          <div><Label>Description</Label><Textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setDlg({ ...dlg, open: false })}>Cancel</Button>
          <Button data-testid="save-cat-btn" onClick={save} disabled={!name.trim()} className="bg-[#F5C242] text-black hover:bg-[#e9a722]">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ItemDialog({ dlg, setDlg, cats, selectedCat, onDone }) {
  const [name, setName] = useState(""); const [sp, setSp] = useState(0); const [unit, setUnit] = useState("pc");
  const [catId, setCatId] = useState(""); const [notes, setNotes] = useState("");
  const [recipe, setRecipe] = useState(""); const [method, setMethod] = useState(""); const [imgUrl, setImgUrl] = useState("");
  const [makingCost, setMakingCost] = useState(""); const [gm, setGm] = useState(""); const [gmp, setGmp] = useState("");
  const [ings, setIngs] = useState([]);
  // Freeze-by-default: when editing an existing item, all fields are locked to
  // prevent accidental keystrokes. User must explicitly hit "Unlock to edit".
  // When adding a new item, fields are unlocked immediately (nothing to protect).
  const [locked, setLocked] = useState(true);
  useEffect(() => {
    const it = dlg.item;
    setName(it?.name || ""); setSp(it?.sp || 0); setUnit(it?.unit || "pc");
    setCatId(it?.category_id || selectedCat?.id || ""); setNotes(it?.notes || "");
    setRecipe(it?.recipe || ""); setMethod(it?.method || ""); setImgUrl(it?.image_url || "");
    setMakingCost(it?.making_cost ?? ""); setGm(it?.gross_margin ?? ""); setGmp(it?.gross_margin_pct ?? "");
    setIngs(it?.ingredients?.length ? [...it.ingredients] : [{ name: "", cost: 0, unit: "", image_url: "" }]);
    setLocked(dlg.mode === "edit");
  }, [dlg, selectedCat]);
  const save = async () => {
    if (locked) return toast.error("Fields are locked — click 'Unlock to edit' first");
    const clean = ings.filter(i => i.name.trim()).map(i => ({
      name: i.name, cost: Number(i.cost) || 0, unit: i.unit || "", image_url: i.image_url || "",
      ...(i.qty ? { qty: Number(i.qty), qty_unit: i.qty_unit || "g" } : {}),
    }));
    const body = { category_id: catId, name, sp: Number(sp), unit, ingredients: clean, notes, recipe, method, image_url: imgUrl,
      making_cost: makingCost === "" ? null : Number(makingCost),
      gross_margin: gm === "" ? null : Number(gm),
      gross_margin_pct: gmp === "" ? null : Number(gmp) };
    try {
      if (dlg.mode === "add") await api.post("/items", body);
      else await api.patch(`/items/${dlg.item.id}`, body);
      toast.success("Saved"); setDlg({ open: false, mode: "add", item: null }); onDone();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })}>
      <DialogContent className="bg-white border-[#E4D6A8] max-w-2xl max-h-[90vh] overflow-y-auto" style={{ color: "#2b1a0d" }}>
        <DialogHeader><DialogTitle className="font-display text-2xl">{dlg.mode === "add" ? "New Item" : "Edit Item"}</DialogTitle></DialogHeader>
        {dlg.mode === "edit" && (
          <div data-testid="item-lock-banner" className={`rounded-lg px-3 py-2 mb-2 flex items-center gap-2 border ${locked ? "bg-[#FFF8E7] border-[#E4D6A8]" : "bg-emerald-50 border-emerald-200"}`}>
            <ShieldCheck size={16} style={{ color: locked ? "#B4841F" : "#0a7a3b" }} />
            <span className="text-xs flex-1" style={{ color: "#5b4530" }}>
              {locked ? "Fields are locked to prevent accidental edits. Click Unlock to enable editing." : "Editing enabled — remember to Save when done."}
            </span>
            <Button data-testid="item-lock-toggle" size="sm" variant="outline"
              onClick={() => setLocked(v => !v)}
              className={locked ? "bg-[#F5C242] text-black hover:bg-[#e9a722] border-transparent" : "border-emerald-300 text-emerald-800"}>
              {locked ? "Unlock to edit" : "Re-lock"}
            </Button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Item Name</Label><Input data-testid="item-name-input" value={name} disabled={locked} onChange={e => setName(e.target.value)} /></div>
          <div><Label>Selling Price (₹)</Label><Input type="number" step="0.01" value={sp} disabled={locked} onChange={e => setSp(e.target.value)} /></div>
          <div><Label>Unit</Label><Input value={unit} disabled={locked} onChange={e => setUnit(e.target.value)} placeholder="pc, ml, g..." /></div>
          <div><Label>Making Cost (₹)</Label><Input data-testid="item-making-cost-input" type="number" step="0.01" value={makingCost} disabled={locked} onChange={e => setMakingCost(e.target.value)} /></div>
          <div><Label>Gross Margin (₹)</Label><Input data-testid="item-gm-input" type="number" step="0.01" value={gm} disabled={locked} onChange={e => setGm(e.target.value)} /></div>
          <div className="col-span-2"><Label>Gross Margin %</Label><Input data-testid="item-gmp-input" type="number" step="0.01" value={gmp} disabled={locked} onChange={e => setGmp(e.target.value)} placeholder="e.g. 57.09" /></div>
          <div className="col-span-2"><Label>Category</Label>
            <Select value={catId} onValueChange={setCatId} disabled={locked}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Item image</Label>
            <fieldset disabled={locked} className={locked ? "opacity-60 pointer-events-none" : ""}>
              <ImageUploadField value={imgUrl} onChange={setImgUrl} testid="item-image" />
            </fieldset>
          </div>
          <div className="col-span-2">
            <Label>Ingredients / Costing components</Label>
            <div className="space-y-2 mt-2">
              {ings.map((ing, i) => (
                <div key={i} className="border border-[#E4D6A8] rounded-lg p-2 space-y-2">
                  <div className="grid grid-cols-[1fr_70px_60px_80px_70px_32px] gap-2 items-center">
                    <Input placeholder="Name" value={ing.name} disabled={locked} onChange={e => { const c = [...ings]; c[i].name = e.target.value; setIngs(c); }} />
                    <Input type="number" step="any" placeholder="Qty" value={ing.qty ?? ""} disabled={locked} title="Qty used (for master auto-cost)"
                      onChange={e => { const c = [...ings]; c[i].qty = e.target.value === "" ? null : Number(e.target.value); setIngs(c); }} />
                    <select value={ing.qty_unit || "g"} disabled={locked} className="h-9 px-1 rounded border bg-white text-xs" style={{ borderColor: "#E4D6A8" }}
                      onChange={e => { const c = [...ings]; c[i].qty_unit = e.target.value; setIngs(c); }}>
                      <option value="g">g</option><option value="ml">ml</option><option value="pc">pc</option>
                    </select>
                    <Input type="number" step="0.01" placeholder="Cost ₹" value={ing.cost} disabled={locked} onChange={e => { const c = [...ings]; c[i].cost = e.target.value; setIngs(c); }} />
                    <Input placeholder="Unit" value={ing.unit} disabled={locked} onChange={e => { const c = [...ings]; c[i].unit = e.target.value; setIngs(c); }} />
                    <Button variant="ghost" size="sm" disabled={locked} onClick={() => setIngs(ings.filter((_, j) => j !== i))}><X size={14} /></Button>
                  </div>
                  <fieldset disabled={locked} className={locked ? "opacity-60 pointer-events-none" : ""}>
                    <ImageUploadField value={ing.image_url || ""} onChange={(v) => { const c = [...ings]; c[i].image_url = v; setIngs(c); }} testid={`ing-image-${i}`} compact />
                  </fieldset>
                </div>
              ))}
              <Button variant="outline" size="sm" disabled={locked} onClick={() => setIngs([...ings, { name: "", cost: 0, unit: "", image_url: "" }])}>
                <Plus size={14} className="mr-1" /> Add ingredient
              </Button>
            </div>
          </div>
          <div className="col-span-2"><Label>Recipe</Label><Textarea data-testid="item-recipe-input" value={recipe} disabled={locked} onChange={e => setRecipe(e.target.value)} rows={3} placeholder="Full recipe details" /></div>
          <div className="col-span-2"><Label>Method / Steps</Label><Textarea data-testid="item-method-input" value={method} disabled={locked} onChange={e => setMethod(e.target.value)} rows={4} placeholder="1. Step one&#10;2. Step two..." /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea value={notes} disabled={locked} onChange={e => setNotes(e.target.value)} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setDlg({ ...dlg, open: false })}>Cancel</Button>
          <Button data-testid="save-item-btn" onClick={save} disabled={locked || !name.trim() || !catId} className="bg-[#F5C242] text-black hover:bg-[#e9a722]">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TrendingIngredients() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [alert, setAlert] = useState(null);
  const [quoteDlg, setQuoteDlg] = useState({ open: false, ingredient: "" });
  const reload = () => api.get(`/reports/trending-ingredients?days=${days}&limit=10`).then(r => setData(r.data));
  useEffect(() => { reload(); }, [days]);
  const checkAlert = async () => {
    try {
      const { data } = await api.post("/reports/trending-alerts/check");
      setAlert(data);
      if (data.changed) toast.success(`New #1: ${data.current} — admin notified by email`);
      else toast.info(`Still #1: ${data.current || "—"}`);
    } catch { toast.error("Check failed"); }
  };
  if (!data) return null;
  return (
    <div className="rounded-2xl border border-[#E4D6A8] bg-white p-6" data-testid="trending-ingredients">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="font-display text-2xl" style={{ color: "#2b1a0d" }}>Trending Ingredients</h2>
          <p className="text-sm" style={{ color: "#7a6349" }}>Ingredients receiving the most discrepancy notes — renegotiate these vendors first.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="check-trending-alert" size="sm" variant="outline" onClick={checkAlert}
            style={{ borderColor: "#E4D6A8" }}>
            Check #1 alert
          </Button>
          <select data-testid="trending-days" value={days} onChange={e => setDays(Number(e.target.value))}
            className="h-9 px-3 rounded-md border border-[#E4D6A8] bg-white text-sm">
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
        </div>
      </div>
      {alert && (
        <div className="mb-3 p-3 rounded-lg text-sm" style={{ background: alert.changed ? "#FCE9C6" : "#F5EFE0", color: "#5b3512" }}>
          {alert.changed
            ? <>🚨 <b>{alert.current}</b> is the new #1 (was {alert.previous || "—"}). Admin has been emailed.</>
            : <>Current #1: <b>{alert.current || "—"}</b> · no change since last check.</>}
        </div>
      )}
      {data.rows.length === 0 ? (
        <div className="text-sm" style={{ color: "#7a6349" }}>No ingredient-level suggestions in the selected window yet.</div>
      ) : (
        <ol className="space-y-2">
          {data.rows.map((r, i) => (
            <li key={r.ingredient_name} data-testid={`trending-${r.ingredient_name.toLowerCase().replace(/\s+/g,'-')}`}
              className="flex items-center gap-3 p-3 rounded-lg border" style={{ borderColor: "#E4D6A8" }}>
              <span className="font-display text-2xl w-10 text-center" style={{ color: "#B4841F" }}>#{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold" style={{ color: "#2b1a0d" }}>{r.ingredient_name}</div>
                <div className="text-xs mt-0.5 truncate" style={{ color: "#7a6349" }}>
                  in {r.items.join(" · ")}{r.items.length >= 5 ? " …" : ""}
                </div>
              </div>
              <Badge className="bg-[#F5C242] text-black">{r.count} note{r.count === 1 ? "" : "s"}</Badge>
              {r.open_count > 0 && <Badge className="bg-orange-100 text-orange-700">{r.open_count} open</Badge>}
              <Button data-testid={`vendor-quotes-${r.ingredient_name.toLowerCase().replace(/\s+/g,'-')}`} size="sm"
                onClick={() => setQuoteDlg({ open: true, ingredient: r.ingredient_name })}
                className="bg-[#3B2519] hover:bg-[#2a1a11] text-[#F5C242]">
                Vendors
              </Button>
            </li>
          ))}
        </ol>
      )}
      <VendorQuotesDialog dlg={quoteDlg} setDlg={setQuoteDlg} />
    </div>
  );
}

function VendorQuotesDialog({ dlg, setDlg }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ vendor_name: "", quote_price: "", quote_unit: "", notes: "" });
  const load = () => {
    if (!dlg.ingredient) return;
    api.get(`/vendor-quotes?ingredient_name=${encodeURIComponent(dlg.ingredient)}`).then(r => setRows(r.data));
  };
  useEffect(() => { if (dlg.open) { load(); setForm({ vendor_name: "", quote_price: "", quote_unit: "", notes: "" }); } }, [dlg.open, dlg.ingredient]);
  const add = async () => {
    if (!form.vendor_name.trim() || form.quote_price === "") return toast.error("Vendor and price required");
    try {
      await api.post("/vendor-quotes", { ingredient_name: dlg.ingredient, ...form, quote_price: Number(form.quote_price) });
      toast.success("Quote saved"); load();
      setForm({ vendor_name: "", quote_price: "", quote_unit: "", notes: "" });
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    await api.delete(`/vendor-quotes/${id}`); toast.success("Deleted"); load();
  };
  const best = rows.length ? rows.reduce((m, r) => r.quote_price < m.quote_price ? r : m, rows[0]) : null;
  return (
    <Dialog open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })}>
      <DialogContent className="bg-white border-[#E4D6A8] max-w-2xl" style={{ color: "#2b1a0d" }}>
        <DialogHeader><DialogTitle className="font-display text-2xl">Vendor Quotes · {dlg.ingredient}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {best && <div className="p-3 rounded-lg text-sm" style={{ background: "#F5EFE0" }}>
            🏆 Best offer so far: <b>{best.vendor_name}</b> at <b>₹{best.quote_price}</b>{best.quote_unit && <> / {best.quote_unit}</>}
          </div>}
          <div className="rounded-lg border overflow-hidden" style={{ borderColor: "#E4D6A8" }}>
            <table className="w-full text-sm">
              <thead className="bg-[#F5EFE0] text-[10px] uppercase font-mono2 tracking-widest" style={{ color: "#7a6349" }}>
                <tr><th className="text-left px-3 py-2">Vendor</th><th className="text-left px-3 py-2">Price</th><th className="text-left px-3 py-2">Unit</th><th className="text-left px-3 py-2">Notes</th><th className="text-left px-3 py-2">Quoted</th><th></th></tr>
              </thead>
              <tbody>
                {rows.map(q => (
                  <tr key={q.id} className="border-t" style={{ borderColor: "#E4D6A8" }} data-testid={`quote-row-${q.id}`}>
                    <td className="px-3 py-2 font-medium">{q.vendor_name}</td>
                    <td className="px-3 py-2 font-mono2">₹{q.quote_price}</td>
                    <td className="px-3 py-2">{q.quote_unit || "—"}</td>
                    <td className="px-3 py-2 text-xs">{q.notes || "—"}</td>
                    <td className="px-3 py-2 text-[10px] font-mono2">{new Date(q.quoted_at).toLocaleDateString()}</td>
                    <td className="px-3 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => del(q.id)}><Trash2 size={14} className="text-red-500" /></Button></td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={6} className="text-center py-4" style={{ color: "#7a6349" }}>No quotes logged yet</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="grid grid-cols-4 gap-2 pt-2 border-t" style={{ borderColor: "#E4D6A8" }}>
            <Input data-testid="quote-vendor" placeholder="Vendor name" value={form.vendor_name} onChange={e => setForm(f => ({...f, vendor_name: e.target.value}))} />
            <Input data-testid="quote-price" type="number" step="0.01" placeholder="Price ₹" value={form.quote_price} onChange={e => setForm(f => ({...f, quote_price: e.target.value}))} />
            <Input data-testid="quote-unit" placeholder="Unit (kg/L)" value={form.quote_unit} onChange={e => setForm(f => ({...f, quote_unit: e.target.value}))} />
            <Input data-testid="quote-notes" placeholder="Notes" value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setDlg({ ...dlg, open: false })}>Close</Button>
          <Button data-testid="add-quote-btn" onClick={add} className="bg-[#F5C242] text-black hover:bg-[#e9a722]">Add Quote</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OutletMap({ outlets }) {
  // Simple SVG mini-map: normalise lat/lng to a viewbox and render dots + radius rings.
  if (!outlets.length) return null;
  const lats = outlets.map(o => o.lat); const lngs = outlets.map(o => o.lng);
  const pad = 0.02;
  const minLat = Math.min(...lats) - pad, maxLat = Math.max(...lats) + pad;
  const minLng = Math.min(...lngs) - pad, maxLng = Math.max(...lngs) + pad;
  const W = 720, H = 380;
  const toXY = (lat, lng) => ({
    x: ((lng - minLng) / (maxLng - minLng)) * W,
    y: H - ((lat - minLat) / (maxLat - minLat)) * H
  });
  // approx: 1 degree lat ≈ 111km; scale radius_m to viewbox pixels using the lat span
  const kmPerDegLat = 111;
  const latDeg = maxLat - minLat;
  const pxPerMetre = (H / (latDeg * kmPerDegLat * 1000));
  return (
    <div className="rounded-2xl border border-[#E4D6A8] overflow-hidden bg-white p-4">
      <div className="text-[10px] font-mono2 uppercase tracking-widest mb-2" style={{ color: "#7a6349" }}>
        Outlet map · {outlets.length} stores · circles show geofence radius
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ background: "#FFF8E7" }}>
        {/* soft grid */}
        {[0,1,2,3,4].map(i => (
          <line key={`v${i}`} x1={i*W/4} y1={0} x2={i*W/4} y2={H} stroke="#ECD9A5" strokeWidth="1" />
        ))}
        {[0,1,2,3].map(i => (
          <line key={`h${i}`} x1={0} y1={i*H/3} x2={W} y2={i*H/3} stroke="#ECD9A5" strokeWidth="1" />
        ))}
        {outlets.map(o => {
          const { x, y } = toXY(o.lat, o.lng);
          const r = Math.max(6, o.radius_m * pxPerMetre);
          return (
            <g key={o.id} data-testid={`map-pin-${o.id}`}>
              <circle cx={x} cy={y} r={r} fill="#F5C242" fillOpacity="0.18" stroke="#B4841F" strokeDasharray="4 4" />
              <circle cx={x} cy={y} r={5} fill="#3B2519" />
              <text x={x + 8} y={y - 8} fontSize="11" fill="#2b1a0d" style={{ fontFamily: "Sora, sans-serif", fontWeight: 700 }}>
                {o.name}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// DailyEntry component extracted to /components/ops/DailyEntry.jsx (shared with outlet-manager Viewer)
// PivotBuilder + CommandCentreTab extracted to /components/ops/*.jsx

function ReportsTab() {
  const [rows, setRows] = useState([]);
  const [acks, setAcks] = useState([]);
  const [outlets, setOutlets] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/reports/outlets"), api.get("/terms/acknowledgements"), api.get("/outlets")])
      .then(([r, a, o]) => { setRows(r.data); setAcks(a.data); setOutlets(o.data); });
  }, []);
  const downloadXlsx = async () => {
    const t = localStorage.getItem("cc_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/export.xlsx`, {
      headers: { Authorization: `Bearer ${t}` }
    });
    if (!res.ok) return toast.error("Export failed");
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `chaichaska-reports-${new Date().toISOString().slice(0,10)}.xlsx`;
    link.click();
    toast.success("Report downloaded");
  };
  return (
    <div className="space-y-8">
      <TrendingIngredients />
      <OutletMap outlets={outlets} />

      <div>
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-3xl" style={{ color: "#2b1a0d" }}>Per-Outlet Report</h2>
          <Button data-testid="export-report-btn" onClick={downloadXlsx} className="rounded-full bg-[#F5C242] text-black hover:bg-[#e9a722]">
            <FileText size={14} className="mr-1" /> Download Excel
          </Button>
        </div>
        <p className="text-sm mb-4" style={{ color: "#7a6349" }}>Users, active partners and suggestion volume for every geofenced outlet.</p>
        <div className="rounded-2xl border border-[#E4D6A8] overflow-hidden bg-white cc-scroll-x">
          <table className="w-full text-sm" style={{ color: "#2b1a0d" }}>
            <thead className="bg-[#F5EFE0] font-mono2 text-[10px] uppercase tracking-widest" style={{ color: "#7a6349" }}>
              <tr>
                <th className="text-left px-4 py-3">Outlet</th>
                <th className="text-left px-4 py-3">Radius</th>
                <th className="text-left px-4 py-3">Users</th>
                <th className="text-left px-4 py-3">Active</th>
                <th className="text-left px-4 py-3">Suggestions</th>
                <th className="text-left px-4 py-3">Open</th>
                <th className="text-left px-4 py-3">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.outlet.id || `x-${i}`} className="border-t border-[#E4D6A8]" data-testid={`report-row-${r.outlet.id || "unassigned"}`}>
                  <td className="px-4 py-3 font-medium">{r.outlet.name}</td>
                  <td className="px-4 py-3 font-mono2 text-xs">{r.outlet.radius_m ? `${r.outlet.radius_m}m` : "—"}</td>
                  <td className="px-4 py-3">{r.users_count}</td>
                  <td className="px-4 py-3">{r.active_users}</td>
                  <td className="px-4 py-3">{r.suggestions_total}</td>
                  <td className="px-4 py-3">
                    {r.suggestions_open > 0 ? <Badge className="bg-[#F5C242] text-black">{r.suggestions_open}</Badge> : <span style={{ color: "#7a6349" }}>—</span>}
                  </td>
                  <td className="px-4 py-3 font-mono2 text-xs">{r.last_suggestion_at ? formatIST(r.last_suggestion_at) : "—"}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="text-center py-8" style={{ color: "#7a6349" }}>No outlet data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h2 className="font-display text-3xl mb-1" style={{ color: "#2b1a0d" }}>T&C Acknowledgements</h2>
        <p className="text-sm mb-4" style={{ color: "#7a6349" }}>Legal audit trail — every accepted terms with user, IP, timestamp and user-agent.</p>
        <div className="rounded-2xl border border-[#E4D6A8] overflow-hidden bg-white cc-scroll-x">
          <table className="w-full text-sm" style={{ color: "#2b1a0d" }}>
            <thead className="bg-[#F5EFE0] font-mono2 text-[10px] uppercase tracking-widest" style={{ color: "#7a6349" }}>
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">IP</th>
                <th className="text-left px-4 py-3">Version</th>
                <th className="text-left px-4 py-3">Device</th>
              </tr>
            </thead>
            <tbody>
              {acks.map(a => (
                <tr key={a.id} className="border-t border-[#E4D6A8]" data-testid={`ack-row-${a.id}`}>
                  <td className="px-4 py-3 font-mono2 text-xs">{formatIST(a.accepted_at)}</td>
                  <td className="px-4 py-3">{a.user_email}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="text-[10px] uppercase">{a.role}</Badge></td>
                  <td className="px-4 py-3 font-mono2 text-xs">{a.ip || "—"}</td>
                  <td className="px-4 py-3 font-mono2 text-xs">v{a.version}</td>
                  <td className="px-4 py-3 text-xs truncate max-w-[240px]" style={{ color: "#7a6349" }}>{a.user_agent || "—"}</td>
                </tr>
              ))}
              {acks.length === 0 && <tr><td colSpan={6} className="text-center py-8" style={{ color: "#7a6349" }}>No acknowledgements yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ImageUploadField({ value, onChange, testid, compact }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const t = localStorage.getItem("cc_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads`, {
        method: "POST", headers: { Authorization: `Bearer ${t}` }, body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      onChange(data.url); toast.success("Image uploaded");
    } catch (err) { toast.error(err.message || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className={`flex items-center gap-3 ${compact ? "text-xs" : ""}`}>
      {value ? (
        <img src={value} alt="" className={`rounded object-cover border border-[#E4D6A8] ${compact ? "w-10 h-10" : "w-16 h-16"}`} draggable={false} />
      ) : (
        <div className={`rounded flex items-center justify-center border border-dashed border-[#E4D6A8] bg-[#F5EFE0] ${compact ? "w-10 h-10" : "w-16 h-16"}`}>
          <ImagePlus size={compact ? 14 : 20} color="#B4841F" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <label className={`inline-flex items-center gap-1 cursor-pointer rounded-full px-3 py-1 font-mono2 uppercase tracking-widest ${busy ? "opacity-50 pointer-events-none" : ""}`}
          style={{ background: "#F5C242", color: "#1A1200", fontSize: compact ? "10px" : "11px" }}>
          <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`${testid}-input`} />
          {busy ? "Uploading…" : (value ? "Replace" : "Upload image")}
        </label>
        {value && (
          <button type="button" onClick={() => onChange("")}
            className="ml-2 text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#c0392b" }}
            data-testid={`${testid}-remove`}>Remove</button>
        )}
      </div>
    </div>
  );
}

function OutletsTab() {
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState({ open: false, mode: "add", outlet: null });
  const [form, setForm] = useState({ name: "", lat: "", lng: "", radius_m: 1000 });
  const load = () => api.get("/outlets").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const openEdit = (o) => { setDlg({ open: true, mode: "edit", outlet: o }); setForm({ name: o.name, lat: o.lat, lng: o.lng, radius_m: o.radius_m }); };
  const openAdd = () => { setDlg({ open: true, mode: "add", outlet: null }); setForm({ name: "", lat: "", lng: "", radius_m: 1000 }); };
  const save = async () => {
    const body = { name: form.name.trim(), lat: Number(form.lat), lng: Number(form.lng), radius_m: Number(form.radius_m) || 1000 };
    if (!body.name || isNaN(body.lat) || isNaN(body.lng)) return toast.error("Name, lat and lng are required");
    try {
      if (dlg.mode === "add") await api.post("/outlets", body);
      else await api.patch(`/outlets/${dlg.outlet.id}`, body);
      toast.success("Saved"); setDlg({ open: false, mode: "add", outlet: null }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (o) => {
    if (!window.confirm(`Remove outlet "${o.name}"?`)) return;
    await api.delete(`/outlets/${o.id}`); toast.success("Removed"); load();
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-3xl">Outlets</h2>
          <p className="text-sm text-[#7a6349] mt-1">Each franchise user is anchored to one outlet. Login is only allowed within the configured radius.</p>
        </div>
        <Button data-testid="add-outlet-btn" onClick={openAdd} className="rounded-full bg-[#F5C242] text-black hover:bg-[#e9a722]">
          <Plus size={14} className="mr-1" /> New Outlet
        </Button>
      </div>
      <div className="rounded-2xl border border-[#E4D6A8] overflow-hidden cc-scroll-x">
        <table className="w-full text-sm">
          <thead className="bg-[#F5EFE0] text-[#7a6349] font-mono2 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Latitude</th>
              <th className="text-left px-4 py-3">Longitude</th>
              <th className="text-left px-4 py-3">Radius</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(o => (
              <tr key={o.id} className="border-t border-[#E4D6A8]" data-testid={`outlet-row-${o.id}`}>
                <td className="px-4 py-3 font-medium">{o.name}</td>
                <td className="px-4 py-3 font-mono2 text-xs">{o.lat}</td>
                <td className="px-4 py-3 font-mono2 text-xs">{o.lng}</td>
                <td className="px-4 py-3">{o.radius_m}m</td>
                <td className="px-4 py-3 text-right">
                  <Button data-testid={`edit-outlet-${o.id}`} variant="ghost" size="sm" onClick={() => openEdit(o)}><Edit3 size={14} /></Button>
                  <Button data-testid={`del-outlet-${o.id}`} variant="ghost" size="sm" onClick={() => del(o)}><Trash2 size={14} className="text-red-500" /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-[#7a6349]">No outlets yet</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })}>
        <DialogContent className="bg-[#F5EFE0] border-[#E4D6A8] text-[#2b1a0d]">
          <DialogHeader><DialogTitle className="font-display text-2xl">{dlg.mode === "add" ? "New Outlet" : "Edit Outlet"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Outlet name</Label><Input data-testid="outlet-name-input" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="CCC Wakad" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Latitude</Label><Input data-testid="outlet-lat-input" type="number" step="any" value={form.lat} onChange={e => setForm(f => ({...f, lat: e.target.value}))} placeholder="18.598…" /></div>
              <div><Label>Longitude</Label><Input data-testid="outlet-lng-input" type="number" step="any" value={form.lng} onChange={e => setForm(f => ({...f, lng: e.target.value}))} placeholder="73.758…" /></div>
            </div>
            <div><Label>Radius (metres)</Label><Input data-testid="outlet-radius-input" type="number" min={50} max={50000} value={form.radius_m} onChange={e => setForm(f => ({...f, radius_m: e.target.value}))} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDlg({ open: false, mode: "add", outlet: null })}>Cancel</Button>
            <Button data-testid="save-outlet-btn" onClick={save} className="bg-[#F5C242] text-black hover:bg-[#e9a722]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IngredientsTab({ onDone }) {
  const [rows, setRows] = useState([]);
  const [master, setMaster] = useState([]);
  const [mode, setMode] = useState("master");
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState({ open: false, ing: null });
  const [gdlg, setGdlg] = useState({ open: false, ing: null });
  const [pack, setPack] = useState({ open: false, ing: null, unit: "kg", qty: 1, price: "" });
  const [newCost, setNewCost] = useState("");
  const [busy, setBusy] = useState(false);
  const [ren, setRen] = useState(null);
  const [dep, setDep] = useState(null);
  const autoCalc = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/master-ingredients/auto-calc");
      toast.success(`Saved kg/L rate for ${data.created} raw material${data.created === 1 ? "" : "s"} (base skipped)`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const load = () => {
    api.get("/ingredients").then(r => setRows(r.data));
    api.get("/master-ingredients").then(r => setMaster(r.data));
  };
  useEffect(() => { load(); }, []);
  const openEdit = (ing) => { setDlg({ open: true, ing }); setNewCost(ing.cost ?? ""); };
  const openGlobal = (ing) => {
    if (!ing.in_master) return toast.error("Set the purchase pack first (Set pack button)");
    setGdlg({ open: true, ing }); setNewCost(ing.purchase_price ?? "");
  };
  const openPack = (ing) => {
    if (!ing.in_master && ing.calc) return setPack({ open: true, ing, unit: ing.calc.kind, qty: 1, price: ing.calc.rate });
    setPack({ open: true, ing, unit: ing.purchase_unit || "kg", qty: ing.purchase_qty || 1, price: ing.purchase_price ?? "" });
  };
  const savePack = async () => {
    if (!pack.ing.name.trim()) return toast.error("Enter a name");
    if (pack.price === "" || isNaN(Number(pack.price))) return toast.error("Enter a valid price");
    setBusy(true);
    try {
      await api.put(`/master-ingredients/${encodeURIComponent(pack.ing.key || pack.ing.name.trim().toLowerCase())}`, {
        name: pack.ing.name, purchase_unit: pack.unit,
        purchase_qty: Number(pack.qty) || 1, purchase_price: Number(pack.price),
      });
      toast.success("Pack saved"); setPack({ open: false, ing: null, unit: "kg", qty: 1, price: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const apply = async () => {
    if (newCost === "" || isNaN(Number(newCost))) return toast.error("Enter a valid price");
    setBusy(true);
    try {
      const { data } = await api.post("/ingredients/update-price", {
        name: dlg.ing.name,
        unit: dlg.ing.unit || "",
        old_cost: dlg.ing.cost,
        new_cost: Number(newCost)
      });
      toast.success(`Updated ${data.updated_items} item${data.updated_items === 1 ? "" : "s"}`);
      setDlg({ open: false, ing: null }); load(); onDone && onDone();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const applyGlobal = async () => {
    if (newCost === "" || isNaN(Number(newCost))) return toast.error("Enter a valid price");
    setBusy(true);
    try {
      const { data } = await api.post(`/master-ingredients/${encodeURIComponent(gdlg.ing.key)}/apply-price`, {
        new_price: Number(newCost)
      });
      toast.success(`₹${newCost} applied to '${gdlg.ing.name}' — ${data.updated_items} items (${data.exact_qty_updates} exact, ${data.scaled_updates} scaled)`);
      setGdlg({ open: false, ing: null }); load(); onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const filtered = q ? rows.filter(r => r.name.toLowerCase().includes(q.toLowerCase())) : rows;
  const filteredM = q ? master.filter(r => r.name.toLowerCase().includes(q.toLowerCase())) : master;
  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-3xl">Ingredients Master</h2>
          <p className="text-sm text-[#7a6349] mt-1">One master file — update a price once and it cascades to every item automatically.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-1 rounded-full border border-[#E4D6A8] bg-white p-1">
            <button data-testid="ing-mode-master" onClick={() => setMode("master")}
              className={`px-4 h-8 rounded-full text-xs font-mono2 uppercase tracking-widest ${mode === "master" ? "bg-[#F5C242] text-black" : "text-[#7a6349]"}`}>Master file</button>
            <button data-testid="ing-mode-points" onClick={() => setMode("points")}
              className={`px-4 h-8 rounded-full text-xs font-mono2 uppercase tracking-widest ${mode === "points" ? "bg-[#F5C242] text-black" : "text-[#7a6349]"}`}>Price points</button>
          </div>
          {mode === "master" && <>
            <Button data-testid="ing-add-btn" size="sm" onClick={() => setPack({ open: true, ing: { key: "", name: "", isNew: true }, unit: "kg", qty: 1, price: "" })}
              className="bg-[#F5C242] text-black hover:bg-[#e9a722]"><Plus size={14} className="mr-1" /> Add raw material</Button>
            <Button data-testid="ing-autocalc-btn" size="sm" variant="outline" onClick={autoCalc} disabled={busy}>
              <BarChart3 size={14} className="mr-1" /> Save calculated kg/L rates</Button>
          </>}
          <div className="relative w-full max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
            <Input data-testid="ingredient-search" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search ingredient" className="pl-9 bg-white border-[#E4D6A8]" />
          </div>
        </div>
      </div>

      {mode === "master" ? (
        <div className="rounded-2xl border border-[#E4D6A8] overflow-hidden cc-scroll-x" data-testid="ing-master-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F5EFE0] text-[#7a6349] font-mono2 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3">Raw material</th>
                <th className="text-left px-4 py-3">Purchase pack</th>
                <th className="text-left px-4 py-3">Rate / kg · L</th>
                <th className="text-left px-4 py-3">Calculated from recipes</th>
                <th className="text-left px-4 py-3">Used in</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredM.map(r => (
                <tr key={r.key} className="border-t border-[#E4D6A8] hover:bg-[#EFE3C6]" data-testid={`ing-master-${r.key}`}>
                  <td className="px-4 py-3 font-medium whitespace-nowrap">{r.name}
                    <button data-testid={`rename-${r.key}`} onClick={() => setRen(r)} className="ml-2 opacity-50 hover:opacity-100 align-middle" title="Rename"><Edit3 size={13} /></button>
                    {r.is_base && <span className="ml-2 text-[10px] font-mono2 px-2 py-0.5 rounded-full bg-stone-200 text-stone-700">base · manual</span>}
                    {r.qty_linked > 0 && <span className="ml-2 text-[10px] font-mono2 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{r.qty_linked} qty-linked</span>}
                  </td>
                  <td className="px-4 py-3 font-mono2 text-xs">
                    {r.in_master ? (
                      <span className="inline-block px-2 py-1 rounded-lg bg-[#FFF8E7] border" style={{ borderColor: "#E4D6A8" }}>
                        {r.purchase_qty} {r.purchase_unit} @ ₹{Number(r.purchase_price).toFixed(2)}
                      </span>
                    ) : <span className="text-amber-700 text-[11px] font-mono2">not in master file</span>}
                  </td>
                  <td className="px-4 py-3 font-mono2 text-xs" data-testid={`rate-kg-${r.key}`}>
                    {r.in_master && r.rate_per_kg != null ? <b>₹{Number(r.rate_per_kg).toFixed(2)} / {r.rate_kind}</b>
                      : r.in_master && r.rate_per_base > 0 ? `₹${Number(r.rate_per_base).toFixed(2)} / pc` : "—"}
                  </td>
                  <td className="px-4 py-3 font-mono2 text-xs" data-testid={`calc-kg-${r.key}`}>
                    {r.is_base ? <span className="text-stone-500">skipped</span> : r.calc ? (
                      <span title={`${r.calc.points} portion(s)`}>₹{r.calc.rate.toFixed(2)} / {r.calc.kind}
                        {r.calc.min !== r.calc.max && <span className="block text-[10px] text-[#7a6349]">range ₹{r.calc.min}–₹{r.calc.max}</span>}</span>
                    ) : <span className="text-[#7a6349]">—</span>}
                  </td>
                  <td className="px-4 py-3">{r.item_count} items · {r.usage_count} uses</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button data-testid={`set-pack-${r.key}`} size="sm" variant="outline" onClick={() => openPack(r)} className="mr-2">
                      Set pack
                    </Button>
                    <Button data-testid={`update-global-${r.key}`} size="sm" onClick={() => openGlobal(r)} disabled={!r.in_master}
                      className="bg-[#3B2519] text-[#F5C242] hover:bg-black">
                      <TrendingUp size={14} className="mr-1" /> Price change
                    </Button>
                    <Button data-testid={`deploy-${r.key}`} size="sm" variant="outline" onClick={() => setDep(r)} disabled={!r.in_master} className="ml-2">
                      <Send size={14} className="mr-1" /> Deploy
                    </Button>
                  </td>
                </tr>
              ))}
              {filteredM.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[#7a6349]">No ingredients yet</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="rounded-2xl border border-[#E4D6A8] overflow-hidden cc-scroll-x" data-testid="ing-points-table">
        <table className="w-full text-sm">
          <thead className="bg-[#F5EFE0] text-[#7a6349] font-mono2 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Ingredient</th>
              <th className="text-left px-4 py-3">Current Price</th>
              <th className="text-left px-4 py-3">Unit</th>
              <th className="text-left px-4 py-3">Used In</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.key} className="border-t border-[#E4D6A8] hover:bg-[#EFE3C6]" data-testid={`ing-row-${r.key}`}>
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3 font-mono2">₹{Number(r.cost || 0).toFixed(2)}</td>
                <td className="px-4 py-3 text-[#7a6349]">{r.unit || "—"}</td>
                <td className="px-4 py-3">{r.item_count} items</td>
                <td className="px-4 py-3 text-right">
                  <Button data-testid={`update-price-${r.key}`} size="sm" onClick={() => openEdit(r)}
                    className="bg-[#F5C242] text-black hover:bg-[#e9a722]">
                    <TrendingUp size={14} className="mr-1" /> Update Price
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-[#7a6349]">No ingredients yet</td></tr>}
          </tbody>
        </table>
      </div>
      )}

      {/* Pack editor dialog — define purchase pack (e.g. 1 kg @ ₹580) */}
      <Dialog open={pack.open} onOpenChange={(o) => setPack({ ...pack, open: o })}>
        <DialogContent className="bg-[#F5EFE0] border-[#E4D6A8] text-[#2b1a0d]" data-testid="pack-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{pack.ing?.isNew ? "Add Raw Material" : "Set Purchase Pack"}</DialogTitle>
          </DialogHeader>
          {pack.ing && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl border border-[#E4D6A8] bg-[#FFF8E7]">
                <div className="text-[10px] font-mono2 uppercase tracking-widest text-[#7a6349]">Raw material</div>
                {pack.ing.isNew ? (
                  <Input data-testid="new-ing-name" value={pack.ing.name} placeholder="e.g. Amul Butter" autoFocus
                    onChange={e => setPack({ ...pack, ing: { ...pack.ing, name: e.target.value } })} className="bg-white mt-1" />
                ) : <div className="font-display text-2xl mt-1">{pack.ing.name}</div>}
                {!pack.ing.isNew && !pack.ing.in_master && pack.ing.calc && <div className="text-xs mt-1 text-emerald-700">Pre-filled from recipes: ₹{pack.ing.calc.rate} / {pack.ing.calc.kind}</div>}
                <div className="text-xs text-[#7a6349] mt-1">e.g. Amul Butter bought as 1 kg @ ₹580 → rate becomes ₹0.58 per gram, auto-applied to every item using grams of it.</div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Pack qty</Label>
                  <Input data-testid="pack-qty" type="number" step="any" min="0" value={pack.qty} onChange={e => setPack({ ...pack, qty: e.target.value })} className="bg-white" />
                </div>
                <div>
                  <Label>Pack unit</Label>
                  <select data-testid="pack-unit" value={pack.unit} onChange={e => setPack({ ...pack, unit: e.target.value })}
                    className="h-9 w-full px-2 rounded border bg-white" style={{ borderColor: "#E4D6A8" }}>
                    <option value="kg">kg</option><option value="g">g</option>
                    <option value="L">L</option><option value="ml">ml</option>
                    <option value="pc">pc</option>
                  </select>
                </div>
                <div>
                  <Label>Pack price (₹)</Label>
                  <Input data-testid="pack-price" type="number" step="0.01" min="0" value={pack.price} onChange={e => setPack({ ...pack, price: e.target.value })} className="bg-white" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPack({ ...pack, open: false })}>Cancel</Button>
            <Button data-testid="pack-save" onClick={savePack} disabled={busy}
              className="bg-[#F5C242] text-black hover:bg-[#e9a722]">
              {busy ? "Saving…" : "Save pack"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global master price dialog — new purchase price cascades per-gram to every item */}
      <Dialog open={gdlg.open} onOpenChange={(o) => setGdlg({ ...gdlg, open: o })}>
        <DialogContent className="bg-[#F5EFE0] border-[#E4D6A8] text-[#2b1a0d]" data-testid="global-price-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Raw Material Price Change</DialogTitle>
          </DialogHeader>
          {gdlg.ing && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl border border-[#E4D6A8] bg-[#FFF8E7]">
                <div className="text-[10px] font-mono2 uppercase tracking-widest text-[#7a6349]">Raw material · pack</div>
                <div className="font-display text-2xl mt-1" data-testid="gdlg-ingredient-name">{gdlg.ing.name}</div>
                <div className="text-xs text-[#7a6349] mt-1">
                  Current pack: <b>{gdlg.ing.purchase_qty} {gdlg.ing.purchase_unit} @ ₹{Number(gdlg.ing.purchase_price).toFixed(2)}</b> · used in <b>{gdlg.ing.item_count}</b> items ({gdlg.ing.usage_count} entries{gdlg.ing.qty_linked ? `, ${gdlg.ing.qty_linked} qty-linked` : ""})
                </div>
              </div>
              <div>
                <Label>New pack price (₹) — e.g. butter 580 → 600 per kg</Label>
                <Input data-testid="global-price-input" type="number" step="0.01" value={newCost}
                  onChange={e => setNewCost(e.target.value)} autoFocus className="bg-white" />
                <p className="text-[10px] font-mono2 uppercase tracking-widest text-[#7a6349] mt-2">
                  Qty-linked entries recompute exactly (qty × new rate). Others scale proportionally. Making Cost + Gross Margin recalc automatically.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGdlg({ open: false, ing: null })}>Cancel</Button>
            <Button data-testid="apply-global-price-btn" onClick={applyGlobal} disabled={busy}
              className="bg-[#3B2519] text-[#F5C242] hover:bg-black">
              {busy ? "Applying…" : "Apply to all items"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RenameIngDialog ing={ren} onClose={() => setRen(null)} onDone={() => { load(); onDone && onDone(); }} />
      <DeployIngDialog ing={dep} onClose={() => setDep(null)} onDone={() => { load(); onDone && onDone(); }} />

      <Dialog open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })}>
        <DialogContent className="bg-[#F5EFE0] border-[#E4D6A8] text-[#2b1a0d]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Update Ingredient Price</DialogTitle>
          </DialogHeader>
          {dlg.ing && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl border border-[#E4D6A8] bg-[#FFF8E7]">
                <div className="text-[10px] font-mono2 uppercase tracking-widest text-[#7a6349]">Ingredient · Portion</div>
                <div className="font-display text-2xl mt-1" data-testid="dlg-ingredient-name">
                  {dlg.ing.name}
                  {dlg.ing.unit && <span className="text-[#F5C242] ml-2">· {dlg.ing.unit}</span>}
                </div>
                <div className="text-xs text-[#7a6349] mt-1">
                  Currently ₹{Number(dlg.ing.cost || 0).toFixed(2)} · used in <b>{dlg.ing.item_count}</b> items at this exact price
                </div>
              </div>
              <div>
                <Label>New price (₹)</Label>
                <Input data-testid="new-price-input" type="number" step="0.01" value={newCost}
                  onChange={e => setNewCost(e.target.value)} autoFocus className="bg-white" />
                <p className="text-[10px] font-mono2 uppercase tracking-widest text-[#7a6349] mt-2">
                  This will update <b>{dlg.ing.item_count}</b> item{dlg.ing.item_count === 1 ? "" : "s"} and recalculate their Making Cost + Gross Margin.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDlg({ open: false, ing: null })}>Cancel</Button>
            <Button data-testid="apply-price-btn" onClick={apply} disabled={busy}
              className="bg-[#F5C242] text-black hover:bg-[#e9a722]">
              {busy ? "Updating…" : "Apply everywhere"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RenameIngDialog({ ing, onClose, onDone }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setName(ing?.name || ""); }, [ing]);
  const save = async () => {
    if (!name.trim()) return toast.error("Enter a name");
    setBusy(true);
    try {
      const { data } = await api.post(`/master-ingredients/${encodeURIComponent(ing.key)}/rename`, { new_name: name });
      toast.success(`Renamed in ${data.updated_items} item${data.updated_items === 1 ? "" : "s"}${data.merged ? " (merged)" : ""}`);
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!ing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#F5EFE0] border-[#E4D6A8] text-[#2b1a0d]" data-testid="rename-ing-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Rename Raw Material</DialogTitle></DialogHeader>
        <Label>New name</Label>
        <Input data-testid="rename-ing-input" value={name} onChange={e => setName(e.target.value)} autoFocus className="bg-white" />
        <p className="text-xs text-[#7a6349]">Updates the name in all {ing?.item_count || 0} items using it. If the name already exists, both are merged.</p>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="rename-ing-save" onClick={save} disabled={busy} className="bg-[#F5C242] text-black hover:bg-[#e9a722]">{busy ? "Saving…" : "Rename"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeployIngDialog({ ing, onClose, onDone }) {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState([]);
  const [q, setQ] = useState("");
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("g");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!ing) { return; }
    setSel([]); setQ(""); setQty("");
    setUnit({ kg: "g", g: "g", L: "ml", l: "ml", ml: "ml" }[ing.purchase_unit] || "pc");
    api.get("/items").then(r => setItems(r.data));
  }, [ing]);
  const rate = ing ? (ing.rate_per_base || 0) * ({ kg: 1000, L: 1000, l: 1000 }[unit] || 1) : 0;
  const shown = q ? items.filter(i => i.name.toLowerCase().includes(q.toLowerCase())) : items;
  const toggle = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const save = async () => {
    if (!sel.length) return toast.error("Pick at least one item");
    if (!(Number(qty) > 0)) return toast.error("Enter quantity used per item");
    setBusy(true);
    try {
      const { data } = await api.post(`/master-ingredients/${encodeURIComponent(ing.key)}/deploy`, { item_ids: sel, qty: Number(qty), qty_unit: unit });
      toast.success(`Deployed: ${data.added} added, ${data.updated} updated · ₹${data.cost_per_item} each`);
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!ing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#F5EFE0] border-[#E4D6A8] text-[#2b1a0d] max-w-lg" data-testid="deploy-ing-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Deploy {ing?.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Qty per item</Label>
            <Input data-testid="deploy-qty" type="number" step="any" min="0" value={qty} onChange={e => setQty(e.target.value)} className="bg-white" /></div>
          <div><Label>Unit</Label>
            <select data-testid="deploy-unit" value={unit} onChange={e => setUnit(e.target.value)} className="h-9 w-full px-2 rounded border bg-white" style={{ borderColor: "#E4D6A8" }}>
              <option value="g">g</option><option value="kg">kg</option><option value="ml">ml</option><option value="L">L</option><option value="pc">pc</option>
            </select></div>
        </div>
        <p className="text-xs text-[#7a6349]" data-testid="deploy-cost-preview">Cost per item: <b>₹{((Number(qty) || 0) * rate).toFixed(2)}</b> · Making cost & margin recalc automatically.</p>
        <Input data-testid="deploy-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search items" className="bg-white" />
        <div className="max-h-60 overflow-y-auto rounded-xl border border-[#E4D6A8] bg-white">
          {shown.map(i => (
            <label key={i.id} className="flex items-center gap-2 px-3 py-2 border-b border-[#F5EFE0] text-sm cursor-pointer" data-testid={`deploy-item-${i.id}`}>
              <Checkbox checked={sel.includes(i.id)} onCheckedChange={() => toggle(i.id)} /> {i.name}
            </label>
          ))}
        </div>
        <DialogFooter>
          <span className="text-xs mr-auto self-center">{sel.length} selected</span>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="deploy-save" onClick={save} disabled={busy} className="bg-[#3B2519] text-[#F5C242] hover:bg-black">{busy ? "Deploying…" : "Deploy"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserDialog({ dlg, setDlg, cats, onDone }) {
  const open = dlg.open; const editing = dlg.editing;
  const setOpen = (o) => setDlg({ open: o, editing: o ? editing : null });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [store, setStore] = useState("");
  const [allowed, setAllowed] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [canEdit, setCanEdit] = useState(false);
  const [maxDevices, setMaxDevices] = useState(1);
  const [outletScope, setOutletScope] = useState([]);
  const SM_KEYS = ["sm_view","sm_add","sm_edit","sm_deactivate","sm_view_salary","sm_view_bank","sm_view_docs","sm_handover","sm_roster","sm_daily_report","sm_perf_view","sm_perf_export","sm_perf_manage"];
  const SM_MAP = {
    sm_view:         ["staff","view"],
    sm_add:          ["staff","add"],
    sm_edit:         ["staff","edit"],
    sm_deactivate:   ["staff","deactivate"],
    sm_view_salary:  ["staff","view_salary"],
    sm_view_bank:    ["staff","view_bank"],
    sm_view_docs:    ["staff","view_docs"],
    sm_handover:     ["handover","view"],   // handover.view unlocks the module; add/edit follow
    sm_roster:       ["roster","view"],
    sm_daily_report: ["daily_report","add"],
    sm_perf_view:    ["performance","view_staff"],
    sm_perf_export:  ["performance","export"],
    sm_perf_manage:  ["performance","manage_parameters"],
  };
  const _emptyPerms = () => ({
    view_making_cost: true, view_recipe: true, view_method: true, view_suggestions: true,
    cc_daily_entry: false, cc_pnl: false, cc_pivot: false, cc_item_profit: false,
    roi_view: true, roi_edit: false, prep_view: false, prep_edit: false,
    ...Object.fromEntries(SM_KEYS.map(k => [k, false])),
  });
  const [perms, setPerms] = useState(_emptyPerms());
  const toggle = (id) => setAllowed(a => a.includes(id) ? a.filter(x => x !== id) : [...a, id]);
  const toggleAllCats = () => setAllowed(a => a.length === cats.length ? [] : cats.map(c => c.id));
  const togglePerm = (k) => setPerms(p => ({ ...p, [k]: !p[k] }));
  useEffect(() => {
    if (!open) return;
    api.get("/outlets").then(r => setOutlets(r.data));
    if (editing) {
      setEmail(editing.email || ""); setName(editing.name || ""); setStore(editing.store || "");
      setAllowed(editing.allowed_category_ids || []); setOutletId(editing.outlet_id || "");
      setCanEdit(!!editing.can_edit);
      setMaxDevices(Number(editing.max_devices || 1));
      setOutletScope((editing.permissions || {}).outlet_scope || []);
      const p = editing.permissions || {};
      const mods = (p.modules) || {};
      const smFlags = Object.fromEntries(SM_KEYS.map(k => {
        const [mod, act] = SM_MAP[k];
        return [k, !!(mods[mod] && (mods[mod][act] || mods[mod].grant_access))];
      }));
      setPerms({
        view_making_cost: p.view_making_cost !== false,
        view_recipe: p.view_recipe !== false,
        view_method: p.view_method !== false,
        view_suggestions: p.view_suggestions !== false,
        cc_daily_entry: p.cc_daily_entry === true,
        cc_pnl: p.cc_pnl === true,
        cc_pivot: p.cc_pivot === true,
        cc_item_profit: p.cc_item_profit === true,
        roi_view: p.roi_view !== false,
        roi_edit: p.roi_edit === true,
        prep_view: p.prep_view === true,
        prep_edit: p.prep_edit === true,
        ...smFlags,
      });
    } else {
      setEmail(""); setName(""); setStore(""); setAllowed([]); setOutletId(""); setCanEdit(false);
      setMaxDevices(1);
      setOutletScope([]);
      setPerms(_emptyPerms());
    }
  }, [open, editing]);  // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    // Build permissions.modules from the flat sm_* toggles so the backend
    // (_has_module_perm reads permissions.modules[module][action]) works.
    const modules = {};
    for (const k of SM_KEYS) {
      if (!perms[k]) continue;
      const [mod, act] = SM_MAP[k];
      modules[mod] = { ...(modules[mod] || {}), [act]: true };
      // handover: view auto-implies add+edit for practical use
      if (k === "sm_handover") modules[mod] = { ...modules[mod], add: true, edit: true };
      if (k === "sm_roster")   modules[mod] = { ...modules[mod], add: true, edit: true, copy_whatsapp: true };
      if (k === "sm_daily_report") modules[mod] = { ...modules[mod], view: true, edit: true };
    }
    const permissions = { ...perms, can_edit: canEdit, modules, outlet_scope: outletScope };
    try {
      if (editing) {
        await api.patch(`/users/${editing.id}`, {
          name, store, allowed_category_ids: allowed, outlet_id: outletId || null,
          can_edit: canEdit, max_devices: Number(maxDevices) || 1, permissions
        });
        toast.success("Access updated");
      } else {
        await api.post("/users", { email, name, store, allowed_category_ids: allowed, outlet_id: outletId || null,
          can_edit: canEdit, max_devices: Number(maxDevices) || 1, permissions });
        toast.success("User added");
      }
      setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const PERM_ROWS = [
    { k: "view_making_cost", label: "View Making Cost & Gross Margin", hint: "Show item making cost and gross margin figures" },
    { k: "view_recipe",       label: "View Recipe details",             hint: "Show recipe text on item pages" },
    { k: "view_method",       label: "View Method / Preparation steps", hint: "Show cooking steps and preparation method" },
    { k: "view_suggestions",  label: "Send discrepancy suggestions",    hint: "Allow this user to report price / ingredient discrepancies" },
    { k: "prep_view",         label: "Preparation Method tab · View",   hint: "See the Preparation Method tab with every item's steps" },
    { k: "prep_edit",         label: "Preparation Method tab · Add / Edit", hint: "Write or change preparation methods for their allowed categories" },
    { k: "cc_daily_entry",    label: "Command Centre · Daily Entry",     hint: "Log EOD sales & live expenses for their outlet", group: "cc" },
    { k: "cc_pnl",            label: "Command Centre · P&L waterfall",   hint: "See revenue → variable → contribution → fixed → EBITDA for their outlet", group: "cc" },
    { k: "cc_item_profit",    label: "Command Centre · Item profit",     hint: "Enter units-sold per item and see per-item net profit", group: "cc" },
    { k: "cc_pivot",          label: "Command Centre · Pivot analysis",  hint: "Run universal Row × Col × Metric pivots (advanced)", group: "cc" },
    { k: "roi_view",          label: "ROI Calculator · View",            hint: "See outlet profitability & ROI calculator for assigned outlets", group: "cc" },
    { k: "roi_edit",          label: "ROI Calculator · Edit figures",    hint: "Change sales / expense / capex figures on the ROI calculator", group: "cc" },
    // ---- Staff Management module ----
    { k: "sm_view",           label: "Staff · View list",                hint: "See the staff master (name, designation, outlet)", group: "sm" },
    { k: "sm_add",            label: "Staff · Add new joinees",          hint: "Create new staff records and joining form", group: "sm" },
    { k: "sm_edit",           label: "Staff · Edit details",             hint: "Update staff basic info, addresses, contact", group: "sm" },
    { k: "sm_deactivate",     label: "Staff · Deactivate / mark left",   hint: "Move staff to Inactive status", group: "sm" },
    { k: "sm_view_salary",    label: "Staff · View salary",              hint: "Reveal salary / CTC on the staff card", group: "sm" },
    { k: "sm_view_bank",      label: "Staff · View bank details",        hint: "Reveal bank account, IFSC and cheque proof", group: "sm" },
    { k: "sm_view_docs",      label: "Staff · View documents",           hint: "Access uploaded Aadhaar / PAN / medical etc.", group: "sm" },
    { k: "sm_handover",       label: "Staff · Handover (uniform/asset)", hint: "Record and manage uniform / asset handover entries", group: "sm" },
    { k: "sm_roster",         label: "Staff · Duty Roster",              hint: "Create weekly rosters and share on WhatsApp", group: "sm" },
    { k: "sm_daily_report",   label: "Staff · Daily performance report", hint: "Submit end-of-day performance scores per staff", group: "sm" },
    { k: "sm_perf_view",      label: "Staff · Performance dashboard",    hint: "View performance zones (red/yellow/green) and rankings", group: "sm" },
    { k: "sm_perf_export",    label: "Staff · Export performance Excel", hint: "Download the multi-sheet performance workbook", group: "sm" },
    { k: "sm_perf_manage",    label: "Staff · Manage parameters & thresholds", hint: "Change performance weightages and green/yellow thresholds", group: "sm" },
  ];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white border-[#E4D6A8] max-w-2xl max-h-[92vh] overflow-y-auto" style={{ color: "#2b1a0d" }}>
        <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? `Edit access · ${editing.email}` : "Grant Access"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Email address</Label><Input data-testid="new-user-email" type="email" value={email} disabled={!!editing} onChange={e => setEmail(e.target.value)} placeholder="franchise@example.com" /></div>
            <div><Label>Owner name</Label><Input data-testid="user-name-input" value={name} onChange={e => setName(e.target.value)} /></div>
            <div className="sm:col-span-2"><Label>Store / Location</Label><Input value={store} onChange={e => setStore(e.target.value)} /></div>
          </div>

          <div className="rounded-lg border p-3" style={{ borderColor: "#E4D6A8", background: "#FFF8E7" }}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold text-sm" style={{ color: "#2b1a0d" }}>Devices allowed for this user</div>
                <div className="text-xs mt-0.5" style={{ color: "#7a6349" }}>How many distinct devices/browsers this user may sign in from. 3 attempts beyond this limit auto-revoke access.</div>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setMaxDevices(Math.max(1, Number(maxDevices) - 1))}
                  className="w-8 h-8 rounded-full border" style={{ borderColor: "#E4D6A8" }} data-testid="max-devices-dec">−</button>
                <Input data-testid="max-devices-input" type="number" min={1} max={10} value={maxDevices}
                  onChange={e => setMaxDevices(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
                  className="w-16 h-9 text-center" />
                <button type="button" onClick={() => setMaxDevices(Math.min(10, Number(maxDevices) + 1))}
                  className="w-8 h-8 rounded-full border" style={{ borderColor: "#E4D6A8" }} data-testid="max-devices-inc">+</button>
              </div>
            </div>
          </div>

          <div className="rounded-lg border p-3" style={{ borderColor: "#E4D6A8", background: "#FFF8E7" }}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-semibold text-sm" style={{ color: "#2b1a0d" }}>Outlet access</div>
                <div className="text-[11px]" style={{ color: "#7a6349" }}>Which outlets this user can see in Command Centre / ROI / Staff. Empty = none.</div>
              </div>
              <button type="button" data-testid="toggle-all-outlets"
                onClick={() => setOutletScope(s => s.length === outlets.length ? [] : outlets.map(o => o.id))}
                className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#B4841F" }}>
                {outletScope.length === outlets.length ? "Clear all" : "Select all"}
              </button>
            </div>
            <div className="max-h-32 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1 p-2 rounded border bg-white" style={{ borderColor: "#E4D6A8" }} data-testid="outlet-scope-list">
              {outlets.map(o => (
                <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`outlet-scope-${o.id}`}>
                  <Checkbox checked={outletScope.includes(o.id)}
                    onCheckedChange={() => setOutletScope(s => s.includes(o.id) ? s.filter(x => x !== o.id) : [...s, o.id])} />
                  {o.name}
                </label>
              ))}
              {outlets.length === 0 && <div className="text-xs" style={{ color: "#7a6349" }}>No outlets yet</div>}
            </div>
          </div>

          <div className="rounded-lg border p-3" style={{ borderColor: "#E4D6A8", background: "#FFF8E7" }}>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="user-can-edit" checked={canEdit} onCheckedChange={(v) => setCanEdit(!!v)} />
              <div>
                <div className="font-semibold text-sm flex items-center gap-2" style={{ color: "#2b1a0d" }}>
                  <ShieldCheck size={14} style={{ color: "#B4841F" }} /> Grant editing rights
                </div>
                <div className="text-xs mt-0.5" style={{ color: "#7a6349" }}>
                  Editor can update prices, making cost and recipes for their allowed categories. Default is view-only.
                </div>
              </div>
            </label>
          </div>

          <div className="rounded-lg border p-3" style={{ borderColor: "#E4D6A8", background: "#FFF8E7" }}>
            <div className="font-semibold text-sm mb-2" style={{ color: "#2b1a0d" }}>Grant access to each of the following</div>
            <div className="space-y-2">
              {PERM_ROWS.map(row => (
                <label key={row.k} className="flex items-start gap-3 cursor-pointer" data-testid={`perm-row-${row.k}`}>
                  <Checkbox data-testid={`perm-${row.k}`} checked={!!perms[row.k]} onCheckedChange={() => togglePerm(row.k)} />
                  <div>
                    <div className="text-sm font-medium" style={{ color: "#2b1a0d" }}>{row.label}</div>
                    <div className="text-[11px]" style={{ color: "#7a6349" }}>{row.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Category access (leave empty to grant all)</Label>
              <button type="button" onClick={toggleAllCats} data-testid="toggle-all-cats"
                className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#B4841F" }}>
                {allowed.length === cats.length ? "Clear all" : "Select all"}
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1 p-2 rounded border border-[#E4D6A8] bg-[#FFFDF6]">
              {cats.map(c => (
                <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox data-testid={`acl-${c.id}`} checked={allowed.includes(c.id)} onCheckedChange={() => toggle(c.id)} />
                  {c.name}
                </label>
              ))}
              {cats.length === 0 && <div className="text-xs" style={{ color: "#7a6349" }}>No categories yet</div>}
            </div>
            <div className="text-[10px] font-mono2 uppercase tracking-widest mt-1" style={{ color: "#7a6349" }}>{canEdit ? "Editor · can update prices in allowed categories" : "Viewer · can suggest discrepancies only"}</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="save-user-btn" onClick={save} disabled={!editing && !email.trim()} className="bg-[#F5C242] text-black hover:bg-[#e9a722]">{editing ? "Save changes" : "Grant Access"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ------------------------------------------------------------
// NavConfigDialog — Admin: reorder + rename + hide + add sub-items
// ------------------------------------------------------------
function NavConfigDialog({ open, onOpenChange, nav, onSaved }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setItems((nav?.items || []).map(it => ({
      ...it,
      children: (it.children || []).map(c => ({ ...c })),
    })));
  }, [open, nav]);
  const move = (i, dir) => {
    setItems(list => {
      const next = list.slice();
      const j = i + dir;
      if (j < 0 || j >= next.length) return next;
      [next[i], next[j]] = [next[j], next[i]];
      return next.map((it, idx) => ({ ...it, order: idx }));
    });
  };
  const rename = (i, label) => setItems(list => list.map((it, idx) => idx === i ? { ...it, label } : it));
  const toggleHide = (i) => setItems(list => list.map((it, idx) => idx === i ? { ...it, hidden: !it.hidden } : it));
  const addChild = (i) => setItems(list => list.map((it, idx) => idx === i
    ? { ...it, children: [...(it.children || []), { label: "New sub-item", order: (it.children || []).length }] }
    : it));
  const renameChild = (i, ci, label) => setItems(list => list.map((it, idx) => idx === i
    ? { ...it, children: it.children.map((c, cj) => cj === ci ? { ...c, label } : c) }
    : it));
  const delChild = (i, ci) => setItems(list => list.map((it, idx) => idx === i
    ? { ...it, children: it.children.filter((_, cj) => cj !== ci) }
    : it));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/nav-config", { items });
      toast.success("Sidebar updated");
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#E4D6A8] max-w-2xl max-h-[92vh] overflow-y-auto" style={{ color: "#2b1a0d" }} data-testid="nav-config-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Settings size={20} style={{color:"#B4841F"}}/> Configure Sidebar</DialogTitle></DialogHeader>
        <div className="text-xs mb-3" style={{color:"#5b4530"}}>
          Rename any tab, reorder with ↑ ↓, hide from the menu, or add sub-items underneath. The internal key (in grey) stays fixed so the module keeps working.
        </div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={it.key} className="rounded-lg border p-3" style={{borderColor: "#E4D6A8", background: it.hidden ? "#F5EFE0" : "#FFFDF6"}} data-testid={`nav-row-${it.key}`}>
              <div className="flex items-center gap-2">
                <div className="flex flex-col">
                  <button data-testid={`nav-up-${it.key}`} onClick={() => move(i, -1)} disabled={i === 0} className="w-6 h-5 rounded hover:bg-[#F5EFE0] disabled:opacity-30"><ArrowUp size={12}/></button>
                  <button data-testid={`nav-down-${it.key}`} onClick={() => move(i, +1)} disabled={i === items.length - 1} className="w-6 h-5 rounded hover:bg-[#F5EFE0] disabled:opacity-30"><ArrowDown size={12}/></button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono2 uppercase tracking-widest mb-1" style={{color:"#7a6349"}}>key · {it.key}</div>
                  <Input data-testid={`nav-label-${it.key}`} value={it.label} onChange={e => rename(i, e.target.value)} className="h-8" />
                </div>
                <button data-testid={`nav-hide-${it.key}`} onClick={() => toggleHide(i)} className="w-9 h-9 rounded-full border flex items-center justify-center" style={{borderColor:"#E4D6A8"}} title={it.hidden ? "Show" : "Hide"}>
                  {it.hidden ? <EyeOff size={14} style={{color:"#7a6349"}}/> : <Eye size={14} style={{color:"#0a7a3b"}}/>}
                </button>
                <button data-testid={`nav-addchild-${it.key}`} onClick={() => addChild(i)} className="rounded-full px-3 h-9 text-[10px] font-mono2 uppercase tracking-widest bg-[#3B2519] text-[#F5C242]" title="Add sub-item">
                  <Plus size={12} className="inline mr-1"/> Sub
                </button>
              </div>
              {(it.children && it.children.length > 0) && (
                <div className="mt-3 pl-8 space-y-1.5 border-l" style={{borderColor:"#F3E7C4"}}>
                  {it.children.map((c, ci) => (
                    <div key={ci} className="flex items-center gap-2">
                      <span className="text-xs text-[#7a6349]">↳</span>
                      <Input data-testid={`nav-child-${it.key}-${ci}`} value={c.label} onChange={e => renameChild(i, ci, e.target.value)} className="h-7 text-xs" />
                      <button data-testid={`nav-child-del-${it.key}-${ci}`} onClick={() => delChild(i, ci)} className="w-7 h-7 rounded hover:bg-red-50 text-red-500"><Trash2 size={12}/></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="nav-save" onClick={save} disabled={busy} className="bg-[#F5C242] text-black hover:bg-[#e9a722]">
            {busy ? "Saving…" : "Save sidebar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ------------------------------------------------------------
// SessionsTab — Live presence + login/logout history with location
// ------------------------------------------------------------
function SessionsTab() {
  const [live, setLive] = useState({ online: [], away: [] });
  const [history, setHistory] = useState({ rows: [], summary: [] });
  const [view, setView] = useState("live");
  const [emailF, setEmailF] = useState("");
  const [busy, setBusy] = useState(false);
  const loadLive = () => api.get("/sessions/live").then(r => setLive(r.data)).catch(() => {});
  const loadHist = () => api.get("/sessions/history", { params: { days: 30, limit: 200 } })
    .then(r => setHistory(r.data)).catch(() => {});
  useEffect(() => {
    loadLive(); loadHist();
    const t = setInterval(loadLive, 15000);
    return () => clearInterval(t);
  }, []);
  const fmtDur = (m) => {
    if (m == null) return "—";
    if (m < 1) return "<1 min";
    if (m < 60) return `${Math.round(m)} min`;
    return `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`;
  };
  const loc = (r) => [r.city, r.region, r.country].filter(Boolean).join(", ") || "—";
  const filteredHist = emailF ? history.rows.filter(r => r.email.includes(emailF.toLowerCase())) : history.rows;
  return (
    <div data-testid="sessions-tab-root">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-3xl" style={{ color: "#2b1a0d" }}>Sessions &amp; Presence</h2>
          <p className="text-sm mt-1" style={{ color: "#7a6349" }}>Who is on the app right now, plus full login → logout history with location and time spent.</p>
        </div>
        <div className="flex gap-1 rounded-full border border-[#E4D6A8] bg-white p-1">
          <button data-testid="sess-view-live" onClick={() => setView("live")}
            className={`px-4 h-8 rounded-full text-xs font-mono2 uppercase tracking-widest ${view === "live" ? "bg-[#F5C242] text-black" : "text-[#7a6349]"}`}>Live now</button>
          <button data-testid="sess-view-history" onClick={() => setView("history")}
            className={`px-4 h-8 rounded-full text-xs font-mono2 uppercase tracking-widest ${view === "history" ? "bg-[#F5C242] text-black" : "text-[#7a6349]"}`}>History</button>
          <button data-testid="sess-view-summary" onClick={() => setView("summary")}
            className={`px-4 h-8 rounded-full text-xs font-mono2 uppercase tracking-widest ${view === "summary" ? "bg-[#F5C242] text-black" : "text-[#7a6349]"}`}>Summary</button>
        </div>
      </div>

      {view === "live" && (
        <div data-testid="sessions-live">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <div className="rounded-xl p-4 border" style={{ borderColor: "#E4D6A8", background: "#eefbf1" }}>
              <div className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#0a7a3b" }}>Online now</div>
              <div className="font-display text-3xl" style={{ color: "#0a7a3b" }} data-testid="sess-online-count">{live.online.length}</div>
            </div>
            <div className="rounded-xl p-4 border" style={{ borderColor: "#E4D6A8", background: "#FFF8E7" }}>
              <div className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#B4841F" }}>Idle / away</div>
              <div className="font-display text-3xl" style={{ color: "#B4841F" }} data-testid="sess-away-count">{live.away.length}</div>
            </div>
          </div>
          {live.online.length === 0 && live.away.length === 0 && (
            <div className="text-sm text-center py-8 rounded-xl border" style={{ borderColor: "#E4D6A8", color: "#7a6349" }} data-testid="sess-none">Nobody signed in right now.</div>
          )}
          <div className="space-y-2">
            {[...live.online, ...live.away].map(s => (
              <div key={s.id} data-testid={`sess-row-${s.id}`} className="flex items-center gap-4 rounded-xl border p-3 flex-wrap"
                style={{ borderColor: s.online ? "#a7e0b8" : "#E4D6A8", background: s.online ? "#eefbf1" : "#fff" }}>
                <span className={`w-2.5 h-2.5 rounded-full ${s.online ? "bg-emerald-500 animate-pulse" : "bg-amber-400"}`} />
                <div className="flex-1 min-w-[180px]">
                  <div className="font-medium text-sm" style={{ color: "#2b1a0d" }}>{s.name || s.email}
                    {s.role === "admin" && <Badge className="ml-2 bg-[#3B2519] text-[#F5C242] border-transparent">Admin</Badge>}
                  </div>
                  <div className="text-[11px]" style={{ color: "#7a6349" }}>{s.email}</div>
                </div>
                <div className="text-xs text-right" style={{ color: "#3B2519" }}>
                  <div>Since {formatIST(s.login_at)}</div>
                  <div style={{ color: "#7a6349" }}>{loc(s)}</div>
                </div>
                <div className="text-xs font-mono2" style={{ color: s.online ? "#0a7a3b" : "#B4841F" }}>{fmtDur(s.duration_min)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === "history" && (
        <div>
          <div className="mb-3 max-w-xs">
            <Input data-testid="sess-email-filter" placeholder="Filter by email…" value={emailF} onChange={e => setEmailF(e.target.value)} className="bg-white border-[#E4D6A8]" />
          </div>
          <div className="rounded-2xl border border-[#E4D6A8] overflow-hidden cc-scroll-x">
            <table className="w-full text-sm" data-testid="sessions-table">
              <thead className="bg-[#F5EFE0] text-[#7a6349] font-mono2 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Login</th>
                  <th className="text-left px-4 py-3">Logout / Last seen</th>
                  <th className="text-left px-4 py-3">Time spent</th>
                  <th className="text-left px-4 py-3">Location</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody style={{ color: "#2b1a0d" }}>
                {filteredHist.map(s => (
                  <tr key={s.id} className="border-t border-[#E4D6A8]" data-testid={`sess-hist-${s.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{s.name || s.email}</div>
                      <div className="text-[11px]" style={{ color: "#7a6349" }}>{s.email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono2 text-xs">{formatIST(s.login_at)}</td>
                    <td className="px-4 py-3 font-mono2 text-xs">{s.logout_at ? formatIST(s.logout_at) : formatIST(s.last_seen)}</td>
                    <td className="px-4 py-3 font-mono2">{fmtDur(s.duration_min)}</td>
                    <td className="px-4 py-3 text-xs">{loc(s)}</td>
                    <td className="px-4 py-3">
                      {s.active
                        ? <Badge className="bg-emerald-100 text-emerald-700 border-transparent">Live</Badge>
                        : <Badge className="bg-gray-100 text-gray-600 border-transparent">Ended</Badge>}
                    </td>
                  </tr>
                ))}
                {filteredHist.length === 0 && <tr><td colSpan={6} className="text-center py-8" style={{ color: "#7a6349" }}>No sessions in the last 30 days.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === "summary" && (
        <div className="rounded-2xl border border-[#E4D6A8] overflow-hidden cc-scroll-x" data-testid="sessions-summary">
          <table className="w-full text-sm">
            <thead className="bg-[#F5EFE0] text-[#7a6349] font-mono2 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Sessions (30d)</th>
                <th className="text-left px-4 py-3">Total time spent</th>
                <th className="text-left px-4 py-3">Last login</th>
                <th className="text-left px-4 py-3">Last location</th>
              </tr>
            </thead>
            <tbody style={{ color: "#2b1a0d" }}>
              {history.summary.map(u => (
                <tr key={u.email} className="border-t border-[#E4D6A8]" data-testid={`sess-sum-${u.email}`}>
                  <td className="px-4 py-3 font-medium">{u.name || u.email}</td>
                  <td className="px-4 py-3">{u.sessions}</td>
                  <td className="px-4 py-3 font-mono2">{fmtDur(u.total_min)}</td>
                  <td className="px-4 py-3 font-mono2 text-xs">{formatIST(u.last_login)}</td>
                  <td className="px-4 py-3 text-xs">{u.last_city || "—"}</td>
                </tr>
              ))}
              {history.summary.length === 0 && <tr><td colSpan={5} className="text-center py-8" style={{ color: "#7a6349" }}>No data yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
